import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import createEngine, { DiagramModel } from "@projectstorm/react-diagrams";
import { CanvasWidget } from "@projectstorm/react-canvas-core";
import { SolutionType } from "../store/slices/settingsSlice";
import labels from "../Builder/nodes/Logic/helpers/labels";
import actionOptionsConfig from "../Builder/nodes/Logic/Actions/actionOptionsConfig";
import conditionOptionsConfig from "../Builder/nodes/Logic/Conditions/ConditionOptionsConfig";
import { pageProps } from "../helpers/pageProps";
import { BoomIconName } from "../Common/BoomIconNames";
import SolutionFlowNodeFactory from "./flowNodes/SolutionFlowNodeFactory";
import SolutionFlowNodeModel from "./flowNodes/SolutionFlowNodeModel";

const humanReadableLabels = labels as Record<string, string>;

type FlowNodeIcon = BoomIconName | (string & {});

const defaultFlowIcon: BoomIconName =
  "Icon_Library/application icons/library/boomAI_icon.svg2";

const defaultConditionFlowIcon: BoomIconName = "workflows.svg2";

export const actionFlowIconByType: Record<string, FlowNodeIcon> = {
  "start:runtime": defaultFlowIcon,
  "component:start": defaultFlowIcon,
  "stop:runtime": "close.svg2",
  "stop:process": "close.svg2",
  "start:broadcast": "messages-01.svg2",
  "asset:groups-to-variable": defaultFlowIcon,
  "restart:runtime": "icon_repeat-01.svg2",
  setDynamicComms: defaultFlowIcon,
  FlowReturn: defaultFlowIcon,
  setMilestone: "icon_duration-01.svg2",
  "milestone:cancel": "icon_duration-01.svg2",
  "component:accept-package": defaultFlowIcon,
  "component:package": defaultFlowIcon,
  "workflow:contacts-to-variable": defaultFlowIcon,
  "get-filenames": defaultFlowIcon,
  UpdateVariable: "icon_data_variables-01.svg2",
  mergeVariables: "icon_data_variables-01.svg2",
  subtractVariables: "icon_data_variables-01.svg2",
  uniqueVariables: "icon_data_variables-01.svg2",
  ForwardEvent: defaultFlowIcon,
  SetCounter: defaultFlowIcon,
  "variables:move-one-to-another": defaultFlowIcon,
  "variables:delete-one": defaultFlowIcon,
  startedByToVariable: defaultFlowIcon,
  recipientToVariable: defaultFlowIcon,
  StoreReplyToVariable: defaultFlowIcon,
  StoreInputArgumentToDynamicContactSet: defaultFlowIcon,
  "assets:contacts-to-variable": defaultFlowIcon,
  "assets:to-variable": defaultFlowIcon,
  "assets:context-clear": defaultFlowIcon,
  "contact:get-location": defaultFlowIcon,
  "contact:update-location": defaultFlowIcon,
  "form-complete-message": defaultFlowIcon,
  "form-complete-early": defaultFlowIcon,
  "form-message": defaultFlowIcon,
  "form-question": defaultFlowIcon,
  "form:reject": defaultFlowIcon,
  "form:redirect": defaultFlowIcon,
  "form:reply-to-variable": defaultFlowIcon,
  "form:book-event": defaultFlowIcon,
  "time-zone:from-location": defaultFlowIcon,
  editContact: defaultFlowIcon,
  "api:make-request": defaultFlowIcon,
  "extract:from-data-structure": defaultFlowIcon,
  "event:create": defaultFlowIcon,
  "event:find-by-ticket": defaultFlowIcon,
  "event:find-by-reference": defaultFlowIcon,
  "event:find-by-user-reference": defaultFlowIcon,
  "event:assets": defaultFlowIcon,
  "event:locations": defaultFlowIcon,
  "event:update": defaultFlowIcon,
  "event:status-update": defaultFlowIcon,
  "event:tickets": defaultFlowIcon,
  "event:contacts": defaultFlowIcon,
  "event:close-all": defaultFlowIcon,
  "ticket:create": defaultFlowIcon,
  "ticket:load": defaultFlowIcon,
  "ticket:set-status": defaultFlowIcon,
  "reporting:new-entry": defaultFlowIcon,
  "reporting:load-entry": defaultFlowIcon,
  "contact:load": defaultFlowIcon,
  "contact:first-or-create": defaultFlowIcon,
  "contact:delete": defaultFlowIcon,
  "contact:add-to-group": defaultFlowIcon,
  "contact:remove-from-group": defaultFlowIcon,
  AddResponderToDynamicContactSet: defaultFlowIcon,
  RemoveResponderFromDynamicContactSet: defaultFlowIcon,
  AddResponderToContactSet: defaultFlowIcon,
  RemoveResponderFromContactSet: defaultFlowIcon,
  AddStarterToDynamicContactSet: defaultFlowIcon,
  RemoveStarterFromDynamicContactSet: defaultFlowIcon,
  AddStarterToContactSet: defaultFlowIcon,
  RemoveStarterFromContactSet: defaultFlowIcon,
  AddDynamicContactSetToContactSet: defaultFlowIcon,
  RemoveDynamicContactSetFromContactSet: defaultFlowIcon,
  RemoveAllFromDynamicContactSet: defaultFlowIcon,
  RemoveAllFromContactSet: defaultFlowIcon,
  "extractText:inputArgument": defaultFlowIcon,
  "ai:prompt": defaultFlowIcon,
  "add-log-entry": defaultFlowIcon,
};

export const conditionFlowIconByType: Record<string, FlowNodeIcon> = {
  "ticket:exists": defaultConditionFlowIcon,
  "validate:phone-number": defaultConditionFlowIcon,
  "form:reply": defaultConditionFlowIcon,
  "form-validation": defaultConditionFlowIcon,
  "recipients:count": defaultConditionFlowIcon,
  "workflow:contacts-count": defaultConditionFlowIcon,
  "reply:message": defaultConditionFlowIcon,
  broadcastSuccess: defaultConditionFlowIcon,
  escalationAvailable: defaultConditionFlowIcon,
  "variable:text": defaultConditionFlowIcon,
  dateTime: defaultConditionFlowIcon,
  triggerChannel: defaultConditionFlowIcon,
  "contact:has-value": defaultConditionFlowIcon,
  "component:package": defaultConditionFlowIcon,
  isMilestone: defaultConditionFlowIcon,
  "milestone:is-pending": defaultConditionFlowIcon,
  StatusIs: defaultConditionFlowIcon,
  "number:compare": defaultConditionFlowIcon,
  "api:request": defaultConditionFlowIcon,
  "contact:in": defaultConditionFlowIcon,
};

type FlowVariableReference = {
  label: string;
  valueTokens: string[];
  componentUuid?: string;
  flowSelection?: Record<string, any>;
};

type SetupInputReference = {
  flowSelection: Record<string, any>;
  input: any;
};

type SolutionFlowViewMode = "solutionSteps" | "componentFlow";

interface SolutionFlowOverviewProps {
  solution?: SolutionType;
  viewMode?: SolutionFlowViewMode;
  activeStepIndex?: number;
  focusedInputGroupId?: string | null;
  focusedInputNodeKey?: string | null;
  focusedLogicNodeKey?: string | null;
  onNodeClick?: (selection: {
    nodeKind: "solution" | "step" | "group" | "input";
    action?: "edit";
    stepIndex?: number;
    groupId?: string;
    inputIndex?: number;
    inputFocusKey?: string;
    isFullscreen?: boolean;
    clientX?: number;
    clientY?: number;
  }) => void;
}

const SolutionFlowOverview: React.FC<SolutionFlowOverviewProps> = ({
  solution,
  viewMode = "componentFlow",
  activeStepIndex,
  focusedInputGroupId,
  focusedInputNodeKey,
  focusedLogicNodeKey,
  onNodeClick,
}) => {
  const steps = solution?.ComponentConfig?.steps || [];
  const diagramContainerRef = useRef<HTMLDivElement | null>(null);
  const onNodeClickRef = useRef(onNodeClick);
  const diagramNodeClickRef = useRef<(selection: any) => void>(() => {});
  const previousStructureSignatureRef = useRef<string | null>(null);
  const pendingInitialFitRef = useRef(true);
  const animationFrameRef = useRef<number | null>(null);
  const suppressNextInputFocusViewportRef = useRef<string | boolean>(false);
  const focusTargetRef = useRef<
    Record<string, { x: number; y: number; width: number; height: number }>
  >({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const logicConfigContext = useMemo(
    () => ({
      contactFields: [],
      reportingTopics: [],
      reportingTemplates: [],
      jsonCollections: [],
      systemDataStructures: [],
      components: [],
      addChild: () => {},
    }),
    []
  );

  const FOCUS_VIEW_MARGIN = 28;
  const NODE_HORIZONTAL_GAP = 100;
  const showSolutionSteps = viewMode === "solutionSteps";
  const showComponentFlow = viewMode === "componentFlow";

  const LAYOUT = {
    baseX: 60,
    baseY: 220,
    ruleBaseY: 220,
    stepToGroupX: 260 + NODE_HORIZONTAL_GAP,
    groupToInputX: 240 + NODE_HORIZONTAL_GAP,
    componentToEventX: 260 + NODE_HORIZONTAL_GAP,
    roleToComponentX: 260 + NODE_HORIZONTAL_GAP,
    workflowToEventX: 250 + NODE_HORIZONTAL_GAP,
    eventToListenerX: (240 + NODE_HORIZONTAL_GAP) / 2,
    listenerToRuleX: (240 + NODE_HORIZONTAL_GAP) / 2,
    decisionToActionX: 320 + NODE_HORIZONTAL_GAP,
    actionToActionX: 320 + NODE_HORIZONTAL_GAP,
    ruleFalseBranchX: 320 + NODE_HORIZONTAL_GAP,
    ruleFalseBranchDrop: 160,
    listenerRuleBranchGap: 360,
    decisionTrueBranchRise: 140,
    eventBranchGap: 260,
    eventBranchTopPadding: 160,
    componentBranchGap: 130,
    maxInputsPerRow: 5,
    inputHorizontalGap: 240 + NODE_HORIZONTAL_GAP,
    inputVerticalGap: 150,
    groupRowHeight: 120,
    groupVerticalGap: 210,
    groupBottomGap: 90,
    minStepSpan: 220,
    stepVerticalGap: 120,
  };

  const getOrderValue = (item: any, fallbackIndex = 0) => {
    const order =
      item?.Order ??
      item?.order ??
      item?.SortOrder ??
      item?.sortOrder ??
      item?.Position ??
      item?.position;

    return Number.isFinite(Number(order)) ? Number(order) : fallbackIndex;
  };

  const getOrderedArray = (items: any) => {
    return Array.isArray(items)
      ? [...items]
          .map((item, index) => ({ item, index }))
          .sort(
            (firstItem, secondItem) =>
              getOrderValue(firstItem.item, firstItem.index) -
                getOrderValue(secondItem.item, secondItem.index) ||
              firstItem.index - secondItem.index
          )
          .map(({ item }) => item)
      : [];
  };

  const getWorkflowKeyValue = (workflow: any, fallbackIndex = 0) => {
    return String(
      workflow?.UUID ||
        workflow?.ID ||
        workflow?.Label ||
        `workflow:${fallbackIndex}`
    );
  };

  const getWorkflowKeyAliases = (workflow: any, fallbackIndex = 0) => {
    return Array.from(
      new Set(
        [
          getWorkflowKeyValue(workflow, fallbackIndex),
          workflow?.UUID,
          workflow?.ID,
        ]
          .filter((key) => key !== undefined && key !== null && key !== "")
          .map(String)
      )
    );
  };

  const isEntrypointWorkflow = (workflow: any) =>
    workflow?.IsEntrypoint === true;

  const BROADCAST_FOLLOW_UP_EVENTS = [
    "message:reply",
    "outbound-delivery-status",
  ];

  const isBroadcastFollowUpEvent = (eventType: any) => {
    return BROADCAST_FOLLOW_UP_EVENTS.includes(String(eventType || ""));
  };

  const isStartBroadcastAction = (action: any) =>
    action?.Type === "start:broadcast";

  const getStartRuntimeWorkflowKey = (action: any) => {
    if (action?.Type !== "start:runtime") return "";

    return String(
      action?.TypeUUID ||
        action?.WorkflowUUID ||
        action?.WorkflowUuid ||
        action?.workflowUUID ||
        action?.WorkflowID ||
        action?.WorkflowId ||
        action?.workflowId ||
        ""
    );
  };

  const stringifyValue = (value: any): string => {
    if (value === null || value === undefined || value === "") return "";
    if (Array.isArray(value)) {
      return value.map(stringifyValue).filter(Boolean).join(", ");
    }
    if (typeof value === "object") {
      const label =
        value.Label ||
        value.label ||
        value.Name ||
        value.name ||
        value.Value ||
        value.value ||
        value.UUID ||
        value.Uuid;
      if (label) return String(label);

      return Object.entries(value)
        .filter(([, entryValue]) =>
          ["string", "number", "boolean"].includes(typeof entryValue)
        )
        .slice(0, 3)
        .map(([key, entryValue]) => `${key}: ${entryValue}`)
        .join(", ");
    }

    return String(value);
  };

  const getHumanLabel = (value: any): string => {
    const rawValue = stringifyValue(value);

    return rawValue ? humanReadableLabels[rawValue] || rawValue : "";
  };

  const getWorkflowUuid = (workflow: any) => {
    return stringifyValue(
      workflow?.UUID ||
        workflow?.WorkflowUUID ||
        workflow?.WorkflowUuid ||
        workflow?.uuid
    );
  };

  const getConditionTypeKey = (condition: any) => {
    return stringifyValue(
      condition?.ConditionType ||
        condition?.Type ||
        condition?.Condition ||
        condition?.Field ||
        condition?.Kind ||
        condition?.Label
    );
  };

  const getConditionConfigMap = (condition: any, branch?: any) => {
    return conditionOptionsConfig({
      condition: condition || {},
      context: logicConfigContext,
      params: { workflowUUID: getWorkflowUuid(branch?.workflow) },
      event: branch?.eventType || branch?.listener?.EventType,
      pageProps,
    });
  };

  const getActionTypeKey = (action: any) => {
    return stringifyValue(
      action?.ActionType ||
        action?.Type ||
        action?.ActionKind ||
        action?.Kind ||
        action?.SolutionRole ||
        action?.Label
    );
  };

  const getActionIconKeys = (action: any) => {
    return [
      action?.ActionType,
      action?.Type,
      action?.ActionKind,
      action?.Kind,
      action?.SolutionRole,
      action?.Label,
    ]
      .map(stringifyValue)
      .filter(Boolean);
  };

  const getConditionIconKeys = (condition: any) => {
    return [
      condition?.ConditionType,
      condition?.Type,
      condition?.Condition,
      condition?.Field,
      condition?.Kind,
      condition?.Label,
    ]
      .map(stringifyValue)
      .filter(Boolean);
  };

  const getMappedFlowIcon = (
    iconMap: Record<string, FlowNodeIcon>,
    keys: string[]
  ) => {
    const matchingKey = keys.find((key) => iconMap[key]);

    return matchingKey ? iconMap[matchingKey] : undefined;
  };

  const getActionFlowIcon = (action: any) => {
    return getMappedFlowIcon(actionFlowIconByType, getActionIconKeys(action));
  };

  const getConditionFlowIcon = (condition: any) => {
    return getMappedFlowIcon(
      conditionFlowIconByType,
      getConditionIconKeys(condition)
    );
  };

  const getConditionsFlowIcon = (conditions: any[]) => {
    return conditions.map(getConditionFlowIcon).find(Boolean);
  };

  const getActionConfigMap = (action: any, branch?: any) => {
    return actionOptionsConfig({
      action: action || {},
      context: logicConfigContext,
      params: { workflowUUID: getWorkflowUuid(branch?.workflow) },
      rule: branch?.rule || {},
      event: branch?.eventType || branch?.listener?.EventType || {},
      dispatch: () => {},
      pageProps,
    });
  };

  const getActionConfig = (action: any, branch?: any) => {
    const actionKey = getActionTypeKey(action);
    const configMap = getActionConfigMap(action, branch);

    return actionKey ? configMap[actionKey] : undefined;
  };

  const getInputFieldAliases = (field: any) => {
    const fieldKey = stringifyValue(field);
    const aliases = [fieldKey];

    if (/operator$/i.test(fieldKey)) aliases.push("Operator");
    if (/value$/i.test(fieldKey)) aliases.push("value", "Value");
    if (/type$/i.test(fieldKey)) aliases.push("Type");

    return Array.from(new Set(aliases.filter(Boolean)));
  };

  const findInputConfigByField = (inputs: any[] | undefined, field: any) => {
    const aliases = getInputFieldAliases(field).map((alias) =>
      alias.toLowerCase()
    );

    return inputs?.find((input: any) =>
      aliases.includes(stringifyValue(input?.field).toLowerCase())
    );
  };

  const getActionInputConfig = (action: any, field: any, branch?: any): any => {
    return findInputConfigByField(
      getActionConfig(action, branch)?.inputs,
      field
    );
  };

  const getConditionConfig = (condition: any, branch?: any) => {
    const conditionKey = getConditionTypeKey(condition);
    const configMap = getConditionConfigMap(condition, branch);

    return conditionKey ? configMap[conditionKey] : undefined;
  };

  const getConditionInputConfig = (
    condition: any,
    field: any,
    branch?: any
  ): any => {
    return findInputConfigByField(
      getConditionConfig(condition, branch)?.inputs,
      field
    );
  };

  const getOptionLabel = (options: any[] | undefined, value: any) => {
    const rawValue = stringifyValue(value);
    const optionLabel = options?.find((option: any) => {
      const optionValue = stringifyValue(
        option?.value ?? option?.Value ?? option?.id ?? option?.ID
      );

      return optionValue === rawValue;
    });

    const displayLabel = optionLabel
      ? stringifyValue(
          optionLabel?.label ?? optionLabel?.Label ?? optionLabel?.name
        )
      : "";

    return displayLabel || getHumanLabel(rawValue);
  };

  const getConditionFieldLabel = (condition: any, field: any, branch?: any) => {
    return (
      stringifyValue(
        getConditionInputConfig(condition, field, branch)?.props?.label
      ) || getHumanLabel(field)
    );
  };

  const getConditionValueLabel = (
    condition: any,
    field: any,
    value: any,
    branch?: any
  ) => {
    const inputConfig = getConditionInputConfig(condition, field, branch);

    return getOptionLabel(inputConfig?.props?.options, value);
  };

  const getActionLabel = (action: any, actionIndex: number, branch?: any) => {
    const actionTypeLabel = getHumanLabel(getActionTypeKey(action));

    return (
      actionTypeLabel ||
      stringifyValue(getActionConfig(action, branch)?.label) ||
      `Action ${actionIndex + 1}`
    );
  };

  const getActionTypeLabel = (
    action: any,
    actionIndex: number,
    branch?: any
  ) => {
    return (
      stringifyValue(getActionConfig(action, branch)?.label) ||
      getHumanLabel(
        action?.ActionType ||
          action?.Type ||
          action?.ActionKind ||
          action?.Kind ||
          action?.SolutionRole ||
          action?.Label ||
          `Action ${actionIndex + 1}`
      )
    );
  };

  const getConditionTypeLabel = (
    condition: any,
    conditionIndex: number,
    branch?: any
  ) => {
    return (
      stringifyValue(getConditionConfig(condition, branch)?.label) ||
      getHumanLabel(
        getConditionTypeKey(condition) || `Condition ${conditionIndex + 1}`
      )
    );
  };

  const getConditionDetailLabel = (
    condition: any,
    detail: any,
    detailIndex: number,
    branch?: any
  ) => {
    const fieldKey = detail?.Condition || detail?.Field || detail?.Type;
    const operatorKey = detail?.ConditionOperator || detail?.Operator || "";
    const valueKey = detail?.ConditionValue ?? detail?.Value;
    const field = getConditionFieldLabel(condition, fieldKey, branch);
    const operator = getConditionValueLabel(
      condition,
      "Operator",
      operatorKey,
      branch
    );
    const value = getConditionValueLabel(condition, fieldKey, valueKey, branch);
    const parts = [field, operator, value].filter(Boolean);
    return parts.length
      ? parts.join(" ")
      : `Condition detail ${detailIndex + 1}`;
  };

  const getActionFieldLabel = (action: any, field: any, branch?: any) => {
    return (
      stringifyValue(
        getActionInputConfig(action, field, branch)?.props?.label
      ) || getHumanLabel(field)
    );
  };

  const getActionValueLabel = (
    action: any,
    field: any,
    value: any,
    branch?: any
  ) => {
    const inputConfig = getActionInputConfig(action, field, branch);

    return getOptionLabel(inputConfig?.props?.options, value);
  };

  const getActionDetails = (action: any, actionIndex: number, branch?: any) => {
    const options = getOrderedArray(action?.Options);
    const optionDetails = options
      .map((option: any) => {
        const field =
          option?.Key || option?.Type || option?.Operation || option?.Filter;
        const key = getActionFieldLabel(action, field, branch);
        const value = getActionValueLabel(
          action,
          field,
          option?.Value ??
            option?.DefaultValue ??
            option?.Data ??
            option?.ValueFrom,
          branch
        );
        return [key, value].filter(Boolean).join(": ");
      })
      .filter(Boolean);

    const actionType = getActionTypeLabel(action, actionIndex, branch);
    const typeDetail = actionType ? [`Type: ${actionType}`] : [];

    if (optionDetails.length) return [...typeDetail, ...optionDetails];

    return typeDetail.length
      ? typeDetail
      : [getActionLabel(action, actionIndex, branch)];
  };

  const getInputFocusKey = (
    stepIndex: number,
    groupId: string,
    input: any,
    index: number
  ) => {
    if (!groupId) return null;
    const identity =
      input?.UUID ||
      (input?.ComponentUuid && input?.Field
        ? `${input.ComponentUuid}:${input.Field}`
        : null) ||
      (input?.ComponentUuid && input?.Name
        ? `${input.ComponentUuid}:${input.Name}`
        : null) ||
      (input?.ComponentUuid && input?.Label
        ? `${input.ComponentUuid}:${input.Label}`
        : null) ||
      `${index}`;
    return `step:${stepIndex}|group:${groupId}|input:${identity}`;
  };

  const normalizeVariableTokens = (tokens: any[]) => {
    return Array.from(
      new Set(
        tokens.filter(Boolean).map((token) => String(token).toLowerCase())
      )
    );
  };

  const collectSetupVariables = (solutionConfigSteps: any[]) => {
    const variables: FlowVariableReference[] = [];

    solutionConfigSteps.forEach((step: any, stepIndex: number) => {
      (step?.inputGroups || []).forEach((group: any) => {
        (group?.inputs || []).forEach((input: any, inputIndex: number) => {
          const label =
            input?.Label ||
            input?.Name ||
            input?.Field ||
            input?.UUID ||
            "Setup variable";
          const groupId = String(group?.id || "");
          const inputFocusKey = getInputFocusKey(
            stepIndex,
            groupId,
            input,
            inputIndex
          );
          const valueTokens = normalizeVariableTokens([
            input?.UUID,
            input?.Uuid,
            input?.Value,
            input?.ComponentUuid && input?.Field
              ? `${input.ComponentUuid}.${input.Field}`
              : null,
            input?.ComponentUuid && input?.Field
              ? `${input.ComponentUuid}:${input.Field}`
              : null,
            input?.ComponentUuid && input?.UUID
              ? `${input.ComponentUuid}.${input.UUID}`
              : null,
            input?.ComponentUuid && input?.UUID
              ? `${input.ComponentUuid}:${input.UUID}`
              : null,
          ]);

          if (valueTokens.length) {
            variables.push({
              label: String(label),
              valueTokens,
              componentUuid: stringifyValue(input?.ComponentUuid),
              flowSelection: {
                nodeKind: "input",
                action: "edit",
                stepIndex,
                groupId,
                inputIndex,
                inputFocusKey: inputFocusKey || undefined,
              },
            });
          }
        });
      });
    });

    return variables;
  };

  const collectSetupInputReferences = (solutionConfigSteps: any[]) => {
    const referencesByToken = new Map<string, SetupInputReference[]>();

    solutionConfigSteps.forEach((step: any, stepIndex: number) => {
      (step?.inputGroups || []).forEach((group: any) => {
        (group?.inputs || []).forEach((input: any, inputIndex: number) => {
          const groupId = String(group?.id || "");
          const inputFocusKey = getInputFocusKey(
            stepIndex,
            groupId,
            input,
            inputIndex
          );
          const flowSelection = {
            nodeKind: "input" as const,
            action: "edit" as const,
            stepIndex,
            groupId,
            inputIndex,
            inputFocusKey: inputFocusKey || undefined,
          };
          const mergedRefTokens = (input?.MergedInputRefs || []).flatMap(
            (ref: any) => [
              ref?.UUID,
              ref?.Uuid,
              ref?.ComponentUuid && ref?.Field
                ? `${ref.ComponentUuid}.${ref.Field}`
                : null,
              ref?.ComponentUuid && ref?.Field
                ? `${ref.ComponentUuid}:${ref.Field}`
                : null,
              ref?.ComponentUuid && ref?.UUID
                ? `${ref.ComponentUuid}.${ref.UUID}`
                : null,
              ref?.ComponentUuid && ref?.UUID
                ? `${ref.ComponentUuid}:${ref.UUID}`
                : null,
            ]
          );
          const tokens = normalizeVariableTokens([
            input?.UUID,
            input?.Uuid,
            input?.ComponentUuid && input?.Field
              ? `${input.ComponentUuid}.${input.Field}`
              : null,
            input?.ComponentUuid && input?.Field
              ? `${input.ComponentUuid}:${input.Field}`
              : null,
            input?.ComponentUuid && input?.UUID
              ? `${input.ComponentUuid}.${input.UUID}`
              : null,
            input?.ComponentUuid && input?.UUID
              ? `${input.ComponentUuid}:${input.UUID}`
              : null,
            ...mergedRefTokens,
          ]);

          tokens.forEach((token) => {
            const references = referencesByToken.get(token) || [];
            references.push({ flowSelection, input });
            referencesByToken.set(token, references);
          });
        });
      });
    });

    return referencesByToken;
  };

  const collectComponentInputArgumentVariables = (
    solutionValue: SolutionType | undefined,
    setupInputReferences: Map<string, SetupInputReference[]>
  ) => {
    const variables: FlowVariableReference[] = [];
    const seenVariableKeys = new Set<string>();

    const getEditableReference = (
      valueTokens: string[],
      componentUuid: string
    ) => {
      const references = valueTokens.flatMap(
        (token) => setupInputReferences.get(token) || []
      );

      return (
        references.find(
          (reference) =>
            componentUuid &&
            String(reference?.input?.ComponentUuid || "") === componentUuid
        ) || references[0]
      );
    };

    (solutionValue?.Components || []).forEach((component: any) => {
      const componentLabel = stringifyValue(
        component?.Item?.Label || component?.Label || component?.Role
      );
      const componentUuid = stringifyValue(
        component?.ComponentUuid || component?.ItemUuid
      );

      (component?.Item?.InputArguments || []).forEach((argument: any) => {
        const label = stringifyValue(
          argument?.Label || argument?.Name || argument?.Field || argument?.UUID
        );
        const valueTokens = normalizeVariableTokens([
          argument?.UUID,
          argument?.Uuid,
          argument?.Value,
          componentUuid && argument?.Field
            ? `${componentUuid}.${argument.Field}`
            : null,
          componentUuid && argument?.Field
            ? `${componentUuid}:${argument.Field}`
            : null,
          componentUuid && argument?.UUID
            ? `${componentUuid}.${argument.UUID}`
            : null,
          componentUuid && argument?.UUID
            ? `${componentUuid}:${argument.UUID}`
            : null,
        ]);

        if (!valueTokens.length) return;

        const displayLabel = [componentLabel, label || "Variable"]
          .filter(Boolean)
          .join(": ");
        const editableReference = getEditableReference(
          valueTokens,
          componentUuid
        );
        const variable = {
          label: displayLabel || "Variable",
          valueTokens,
          componentUuid,
          flowSelection: editableReference?.flowSelection,
        };

        const variableKey = `${componentUuid}:${valueTokens.join("|")}`;
        if (!seenVariableKeys.has(variableKey)) {
          seenVariableKeys.add(variableKey);
          variables.push(variable);
        }
      });
    });

    return variables;
  };

  const collectVariableReferences = (
    solutionValue: SolutionType | undefined,
    solutionConfigSteps: any[]
  ) => {
    const setupInputReferences =
      collectSetupInputReferences(solutionConfigSteps);

    return [
      ...collectSetupVariables(solutionConfigSteps),
      ...collectComponentInputArgumentVariables(
        solutionValue,
        setupInputReferences
      ),
    ];
  };

  const isVariableValueField = (key: string) => {
    return /(value|uuid)$/i.test(key);
  };

  const isLogicOptionRecord = (value: any) => {
    return (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      (Object.prototype.hasOwnProperty.call(value, "Type") ||
        Object.prototype.hasOwnProperty.call(value, "Key") ||
        Object.prototype.hasOwnProperty.call(value, "ValueFrom")) &&
      (Object.prototype.hasOwnProperty.call(value, "Value") ||
        Object.prototype.hasOwnProperty.call(value, "Data") ||
        Object.prototype.hasOwnProperty.call(value, "DefaultValue"))
    );
  };

  const getVariableValueStrings = (source: any) => {
    const values = new Set<string>();

    const collectValue = (value: any) => {
      if (value === null || value === undefined || value === "") return;
      if (["string", "number", "boolean"].includes(typeof value)) {
        values.add(String(value).toLowerCase());
        return;
      }

      if (Array.isArray(value)) {
        value.forEach(collectValue);
        return;
      }

      if (typeof value === "object") {
        if (isLogicOptionRecord(value)) {
          collectValue(value.Value);
          collectValue(value.Data);
          collectValue(value.DefaultValue);
        }

        Object.entries(value).forEach(([key, entryValue]) => {
          if (isVariableValueField(key)) {
            collectValue(entryValue);
            return;
          }

          if (Array.isArray(entryValue) || typeof entryValue === "object") {
            collectValue(entryValue);
          }
        });
      }
    };

    collectValue(source);

    return Array.from(values);
  };

  const getVariablesUsedIn = (
    source: any,
    setupVariables: FlowVariableReference[],
    componentUuid?: string
  ) => {
    if (!setupVariables.length || !source) return [];
    const valueStrings = getVariableValueStrings(source);
    const variablesBySelection = new Map<string, any>();

    setupVariables
      .filter(
        (variable) =>
          (!componentUuid ||
            !variable.componentUuid ||
            variable.componentUuid === componentUuid) &&
          variable.valueTokens.some(
            (token) =>
              token.length > 1 &&
              valueStrings.some(
                (value) => value === token || value.includes(token)
              )
          )
      )
      .forEach((variable) => {
        variablesBySelection.set(
          variable.flowSelection
            ? JSON.stringify(variable.flowSelection)
            : `${variable.label}:${variable.valueTokens.join("|")}`,
          variable
        );
      });

    return Array.from(variablesBySelection.values()).map((variable) => ({
      label: variable.label,
      flowSelection: variable.flowSelection,
    }));
  };

  const getComponentLogicBranches = (
    solutionValue: SolutionType | undefined
  ) => {
    return (solutionValue?.Components || [])
      .map((component: any, componentIndex: number) => {
        const eventHandlers = getOrderedArray(component?.Item?.EventHandlers);
        const workflows = getOrderedArray(component?.Item?.Workflows);
        const eventHandlersByUuid = new Map<string, any>();
        const eventHandlersById = new Map<string, any>();

        eventHandlers.forEach((eventHandler: any) => {
          if (eventHandler?.UUID) {
            eventHandlersByUuid.set(String(eventHandler.UUID), eventHandler);
          }
          if (eventHandler?.ID !== undefined && eventHandler?.ID !== null) {
            eventHandlersById.set(String(eventHandler.ID), eventHandler);
          }
        });

        const branches = workflows.flatMap(
          (workflow: any, workflowIndex: number) =>
            getOrderedArray(workflow?.EventListeners).flatMap(
              (listener: any, listenerIndex: number) => {
                const eventHandler =
                  (listener?.EventHandlerUUID
                    ? eventHandlersByUuid.get(String(listener.EventHandlerUUID))
                    : null) ||
                  (listener?.EventHandlerID !== undefined &&
                  listener?.EventHandlerID !== null
                    ? eventHandlersById.get(String(listener.EventHandlerID))
                    : null);

                return getOrderedArray(eventHandler?.Rules).map(
                  (rule: any, ruleIndex: number) => ({
                    workflow,
                    workflowKey: getWorkflowKeyValue(workflow, workflowIndex),
                    workflowIndex,
                    listener,
                    listenerIndex,
                    listenerSequence: listenerIndex,
                    eventHandler,
                    eventType: listener?.EventType,
                    listenerOrder: getOrderValue(listener, listenerIndex),
                    rule,
                    ruleIndex,
                  })
                );
              }
            )
        );

        const linkedEventHandlerUuids = new Set(
          branches
            .map((branch: any) => branch.eventHandler?.UUID)
            .filter(Boolean)
            .map(String)
        );
        const fallbackBranches = eventHandlers
          .filter(
            (eventHandler: any) =>
              !eventHandler?.UUID ||
              !linkedEventHandlerUuids.has(String(eventHandler.UUID))
          )
          .flatMap((eventHandler: any, eventHandlerIndex: number) =>
            getOrderedArray(eventHandler?.Rules).map(
              (rule: any, ruleIndex: number) => ({
                workflow: null,
                workflowKey: null,
                workflowIndex: undefined,
                listener: null,
                listenerIndex: eventHandlerIndex,
                listenerSequence: eventHandlerIndex,
                listenerOrder: getOrderValue(eventHandler, eventHandlerIndex),
                eventHandler,
                eventType: eventHandler?.EventType || "Unlinked event",
                rule,
                ruleIndex,
              })
            )
          );

        const allBranches = [...branches, ...fallbackBranches];

        return {
          component,
          componentIndex,
          branches: allBranches,
        };
      })
      .filter((entry) => entry.branches.length > 0);
  };

  const getEventLabel = (branch: any, eventIndex: number) => {
    const eventType = branch?.eventType || branch?.listener?.EventType;
    if (eventType) return getHumanLabel(eventType);

    return `Event ${eventIndex + 1}`;
  };

  const getWorkflowLabel = (branch: any, workflowIndex: number) => {
    return (
      branch?.workflow?.Label ||
      branch?.workflow?.Name ||
      branch?.workflow?.UUID ||
      `Workflow ${workflowIndex + 1}`
    );
  };

  const getComponentRole = (component: any) => {
    return stringifyValue(
      component?.SolutionRole ||
        component?.Role ||
        component?.ComponentRole ||
        component?.Item?.Role
    );
  };

  const getUniqueBranchCount = (
    branches: any[],
    keyGetter: (branch: any, index: number) => string
  ) => {
    return new Set(branches.map(keyGetter).filter(Boolean)).size;
  };

  const getBranchEventKey = (branch: any, index: number) => {
    return String(
      branch?.eventType ||
        branch?.listener?.EventType ||
        branch?.listener?.UUID ||
        branch?.eventHandler?.UUID ||
        index
    );
  };

  const getBranchWorkflowKey = (branch: any, index: number) => {
    return String(
      branch?.workflowKey ||
        branch?.workflow?.UUID ||
        branch?.workflow?.ID ||
        branch?.workflow?.Label ||
        branch?.eventHandler?.WorkflowUUID ||
        `workflow:${index}`
    );
  };

  const getBranchListenerKey = (branch: any, index: number) => {
    return String(
      branch?.listener?.UUID ||
        branch?.listener?.ID ||
        `${branch?.workflow?.UUID || "workflow"}:${
          branch?.eventType || branch?.listener?.EventType || "event"
        }:${branch?.listenerOrder ?? "order"}:${
          branch?.listenerSequence ?? index
        }:${
          branch?.eventHandler?.UUID || branch?.eventHandler?.ID || "handler"
        }`
    );
  };

  const getListenerOrder = (listenerGroup: any, fallbackIndex = 0) => {
    return getOrderValue(
      listenerGroup?.listener || listenerGroup?.sampleBranch?.listener,
      listenerGroup?.listenerSequence ??
        listenerGroup?.sampleBranch?.listenerSequence ??
        fallbackIndex
    );
  };

  const getRuleSignature = (rule: any, ruleIndex: number) => ({
    rule: rule?.UUID || rule?.Label || ruleIndex,
    startRuntimeTargets: getOrderedArray(rule?.Actions)
      .map(getStartRuntimeWorkflowKey)
      .filter(Boolean),
    startsBroadcast: getOrderedArray(rule?.Actions).some(
      isStartBroadcastAction
    ),
    actionCount: rule?.Actions?.length || 0,
    conditionCount:
      rule?.Conditions?.length ||
      rule?.ConditionDetails?.length ||
      rule?.ComponentConfig?.Conditions?.length ||
      0,
  });

  const getBranchStructureSignature = (branch: any, branchIndex: number) => ({
    workflow: branch?.workflow?.UUID || branch?.workflow?.Label || null,
    workflowIsEntrypoint: branch?.workflow?.IsEntrypoint === true,
    eventType: branch?.eventType || branch?.listener?.EventType || null,
    listener:
      branch?.listener?.UUID ||
      branch?.listener?.ID ||
      branch?.listener?.EventHandlerUUID ||
      null,
    listenerOrder:
      branch?.listenerOrder ?? getOrderValue(branch?.listener) ?? null,
    listenerSequence: branch?.listenerSequence ?? null,
    eventHandler:
      branch?.eventHandler?.UUID || branch?.eventHandler?.Label || null,
    ...getRuleSignature(branch?.rule, branchIndex),
  });

  const getComponentStructureSignature = ({ component, branches }: any) => ({
    component:
      component?.ComponentUuid ||
      component?.Item?.UUID ||
      component?.Role ||
      component?.Item?.Label,
    branches: branches.map(getBranchStructureSignature),
  });

  const getRuleConditions = (rule: any) => {
    return getOrderedArray(
      rule?.Conditions ||
        rule?.ConditionDetails ||
        rule?.ComponentConfig?.Conditions ||
        []
    );
  };

  const getRuleActions = (rule: any) => {
    return getOrderedArray(rule?.Actions);
  };

  const getBranchActionsDetails = (actions: any[], branch?: any) => {
    return actions.flatMap((action: any, actionIndex: number) =>
      getActionDetails(action, actionIndex, branch).map(
        (detail: string) => `${actionIndex + 1}. ${detail}`
      )
    );
  };

  const summarizeNodeLabels = (
    labelsToSummarize: string[],
    emptyLabel: string
  ) => {
    const uniqueLabels = Array.from(new Set(labelsToSummarize.filter(Boolean)));
    if (!uniqueLabels.length) return emptyLabel;
    if (uniqueLabels.length <= 2) return uniqueLabels.join(", ");
    return `${uniqueLabels.slice(0, 2).join(", ")} +${
      uniqueLabels.length - 2
    } more`;
  };

  const getConditionNodeTitle = (conditions: any[], branch?: any) => {
    return summarizeNodeLabels(
      conditions.map(
        (condition: any, conditionIndex: number) =>
          `Check ${getConditionTypeLabel(condition, conditionIndex, branch)}`
      ),
      "No condition configured"
    );
  };

  const getActionNodeTitle = (actions: any[], branch?: any) => {
    return summarizeNodeLabels(
      actions.map((action: any, actionIndex: number) =>
        getActionLabel(action, actionIndex, branch)
      ),
      "No action configured"
    );
  };

  const getNodeSize = (node: any) => {
    const kind = node?.options?.nodeKind;
    if (kind === "solution" || kind === "step")
      return { width: 260, height: 120 };
    if (kind === "group" || kind === "input")
      return { width: 240, height: 120 };
    if (kind === "component") return { width: 260, height: 140 };
    if (kind === "workflow") return { width: 250, height: 130 };
    if (kind === "event") return { width: 240, height: 130 };
    if (kind === "condition") return { width: 320, height: 150 };
    if (kind === "decision") return { width: 320, height: 180 };
    if (kind === "actionGroup") return { width: 320, height: 170 };
    return { width: 240, height: 120 };
  };

  const getRuleConditionDetails = (conditions: any[], branch?: any) => {
    return conditions.flatMap((condition: any, conditionIndex: number) => {
      const conditionType = getConditionTypeLabel(
        condition,
        conditionIndex,
        branch
      );
      const typeDetail = conditionType
        ? [`${conditionIndex + 1}. Type: ${conditionType}`]
        : [];

      if (Array.isArray(condition?.ConditionDetails)) {
        return [
          ...typeDetail,
          ...condition.ConditionDetails.map(
            (detail: any, detailIndex: number) =>
              `${conditionIndex + 1}.${
                detailIndex + 1
              }. ${getConditionDetailLabel(
                condition,
                detail,
                detailIndex,
                branch
              )}`
          ),
        ];
      }

      return [
        ...typeDetail,
        `${conditionIndex + 1}. ${getConditionDetailLabel(
          condition,
          condition,
          conditionIndex,
          branch
        )}`,
      ];
    });
  };

  const createFlowLink = (
    sourcePort: any,
    targetPort: any,
    color: string,
    label?: string,
    route: "normal" | "true" | "false" | "rectangular" = "normal"
  ) => {
    const link = sourcePort.link(targetPort);
    Object.assign(link.getOptions() as any, {
      color,
      width: 2,
      curvyness: 80,
    });

    const sourcePosition = sourcePort.getNode?.()?.getPosition?.();
    const targetPosition = targetPort.getNode?.()?.getPosition?.();

    if (sourcePosition && targetPosition) {
      const sourceNodeSize = getNodeSize(sourcePort.getNode());
      const targetNodeSize = getNodeSize(targetPort.getNode());
      const sourceY = sourcePosition.y + sourceNodeSize.height / 2;
      const targetY = targetPosition.y + targetNodeSize.height / 2;
      const sourceLeft = sourcePosition.x;
      const sourceRight = sourcePosition.x + sourceNodeSize.width;
      const sourceBottom = sourcePosition.y + sourceNodeSize.height;
      const sourceAlignment = String(
        sourcePort.getOptions?.()?.alignment || ""
      ).toLowerCase();
      const targetLeft = targetPosition.x;
      const targetRight = targetPosition.x + targetNodeSize.width;
      const isForwardLink = sourceRight <= targetLeft;
      const sourceX =
        sourceAlignment === "bottom"
          ? sourcePosition.x + sourceNodeSize.width / 2
          : (route === "normal" || route === "rectangular") && !isForwardLink
          ? sourceLeft
          : sourceRight;
      const sourceLinkY = sourceAlignment === "bottom" ? sourceBottom : sourceY;
      const targetX =
        (route === "normal" || route === "rectangular") && !isForwardLink
          ? targetRight
          : targetLeft;

      link.getFirstPoint().setPosition(sourceX, sourceLinkY);
      link.getLastPoint().setPosition(targetX, targetY);

      if (route === "rectangular") {
        Object.assign(link.getOptions() as any, {
          curvyness: 0,
        });

        const routeGap = 70;
        const sourceTop = sourcePosition.y;
        const targetTop = targetPosition.y;
        const targetBottom = targetPosition.y + targetNodeSize.height;
        const upperNodeBottom = Math.min(sourceBottom, targetBottom);
        const lowerNodeTop = Math.max(sourceTop, targetTop);
        const hasVerticalGap = lowerNodeTop - upperNodeBottom > routeGap;
        const laneY = hasVerticalGap
          ? upperNodeBottom + (lowerNodeTop - upperNodeBottom) / 2
          : Math.max(sourceBottom, targetBottom) + routeGap;
        const sourceDiagonalRise = Math.abs(laneY - sourceLinkY);
        const targetDiagonalRise = Math.abs(targetY - laneY);
        const diagonalRunRatio = routeGap / Math.max(sourceDiagonalRise, 1);
        const targetDiagonalRun = Math.max(
          routeGap,
          targetDiagonalRise * diagonalRunRatio
        );
        const sourceDiagonalX =
          sourceX + (isForwardLink ? routeGap : -routeGap);
        const targetDiagonalX =
          targetX + (isForwardLink ? -targetDiagonalRun : targetDiagonalRun);

        link.point(sourceDiagonalX, laneY);
        link.point(targetDiagonalX, laneY, 2);
      }
    }

    if (label) link.addLabel(label);
    return link;
  };

  const createComponentLogicNode = ({
    nodeKind,
    title,
    subtitle = "",
    meta = "",
    details,
    variables,
    icon,
    color,
    x,
    y,
    logicNodeKey,
  }: any) => {
    const node = new SolutionFlowNodeModel({
      nodeKind,
      title,
      subtitle,
      meta,
      details,
      variables,
      icon,
      color,
      logicNodeKey,
    });
    node.setPosition(x, y);
    return node;
  };

  const groupBranchesByEvent = (branches: any[]) => {
    const events: any[] = [];
    const eventLookup = new Map<string, any>();

    branches.forEach((branch: any, branchIndex: number) => {
      const eventKey = getBranchEventKey(branch, branchIndex);
      let eventGroup = eventLookup.get(eventKey);
      if (!eventGroup) {
        eventGroup = {
          key: eventKey,
          eventType: branch?.eventType || branch?.listener?.EventType,
          sampleBranch: branch,
          listeners: [],
          listenerLookup: new Map<string, any>(),
        };
        eventLookup.set(eventKey, eventGroup);
        events.push(eventGroup);
      }

      const listenerKey = getBranchListenerKey(branch, branchIndex);
      let listenerGroup = eventGroup.listenerLookup.get(listenerKey);
      if (!listenerGroup) {
        listenerGroup = {
          key: listenerKey,
          workflow: branch?.workflow,
          listener: branch?.listener,
          listenerOrder: branch?.listenerOrder,
          listenerSequence: branch?.listenerSequence,
          eventHandler: branch?.eventHandler,
          sampleBranch: branch,
          rules: [],
        };
        eventGroup.listenerLookup.set(listenerKey, listenerGroup);
        eventGroup.listeners.push(listenerGroup);
      }

      listenerGroup.rules.push(branch);
    });

    return events.map((eventGroup) => ({
      ...eventGroup,
      listeners: eventGroup.listeners
        .map((listenerGroup: any, listenerIndex: number) => ({
          listenerGroup,
          listenerIndex,
        }))
        .sort(
          (firstListener: any, secondListener: any) =>
            getListenerOrder(
              firstListener.listenerGroup,
              firstListener.listenerIndex
            ) -
              getListenerOrder(
                secondListener.listenerGroup,
                secondListener.listenerIndex
              ) || firstListener.listenerIndex - secondListener.listenerIndex
        )
        .map(({ listenerGroup }: any) => listenerGroup),
      listenerLookup: undefined,
    }));
  };

  const groupBranchesByWorkflow = (branches: any[]) => {
    const workflows: any[] = [];
    const workflowLookup = new Map<string, any>();

    branches.forEach((branch: any, branchIndex: number) => {
      const workflowKey = getBranchWorkflowKey(branch, branchIndex);
      let workflowGroup = workflowLookup.get(workflowKey);
      if (!workflowGroup) {
        workflowGroup = {
          key: workflowKey,
          workflow: branch?.workflow,
          sampleBranch: branch,
          branches: [],
        };
        workflowLookup.set(workflowKey, workflowGroup);
        workflows.push(workflowGroup);
      }

      workflowGroup.branches.push(branch);
    });

    return workflows
      .map((workflowGroup, workflowIndex) => ({
        workflowGroup,
        workflowIndex,
      }))
      .sort(
        (firstWorkflow: any, secondWorkflow: any) =>
          Number(!isEntrypointWorkflow(firstWorkflow.workflowGroup.workflow)) -
            Number(
              !isEntrypointWorkflow(secondWorkflow.workflowGroup.workflow)
            ) ||
          getOrderValue(
            firstWorkflow.workflowGroup.workflow,
            firstWorkflow.workflowIndex
          ) -
            getOrderValue(
              secondWorkflow.workflowGroup.workflow,
              secondWorkflow.workflowIndex
            ) ||
          firstWorkflow.workflowIndex - secondWorkflow.workflowIndex
      )
      .map(({ workflowGroup }: any) => ({
        ...workflowGroup,
        events: groupBranchesByEvent(workflowGroup.branches),
      }));
  };

  const getListenerChainEndPorts = (
    branchEndPorts: any[],
    falseEndPorts: any[]
  ) => {
    return [...falseEndPorts, ...branchEndPorts].filter(Boolean);
  };

  const getContinuationPort = (continuation: any) =>
    continuation?.port || continuation;

  const getContinuationLinkOptions = (continuation: any) => ({
    color: continuation?.color || "#4f46e5",
    label: continuation?.label,
    route: continuation?.route || "normal",
  });

  const getPortNodeCenterY = (port: any) => {
    const node = port?.getNode?.();
    const nodePosition = node?.getPosition?.();
    if (!nodePosition) return 0;

    return nodePosition.y + getNodeSize(node).height / 2;
  };

  const getDownwardSourcePort = (sourcePort: any, targetPort: any) => {
    if (getPortNodeCenterY(targetPort) <= getPortNodeCenterY(sourcePort)) {
      return sourcePort;
    }

    return sourcePort.getNode?.()?.getPort?.("actions-down-out") || sourcePort;
  };

  const getRuleBranchOffsetY = (ruleIndex: number) => {
    return ruleIndex * LAYOUT.ruleFalseBranchDrop;
  };

  const hasFalseBranch = (rules: any[], ruleIndex: number) => {
    return ruleIndex < rules.length - 1;
  };

  const getListenerRuleVerticalExtents = (listenerGroup: any) => {
    return (listenerGroup?.rules || []).reduce(
      (
        extents: { top: number; bottom: number },
        branch: any,
        ruleIndex: number
      ) => {
        const offsetY = getRuleBranchOffsetY(ruleIndex);
        const rules = listenerGroup?.rules || [];
        const hasConditions = getRuleConditions(branch?.rule).length > 0;
        const shouldShiftTrueBranch =
          hasConditions && hasFalseBranch(rules, ruleIndex);
        const topY = shouldShiftTrueBranch
          ? offsetY - LAYOUT.decisionTrueBranchRise
          : offsetY;

        return {
          top: Math.min(extents.top, topY),
          bottom: Math.max(extents.bottom, offsetY),
        };
      },
      { top: 0, bottom: 0 }
    );
  };

  const getListenerTopPadding = (listenerGroup: any) => {
    const { top } = getListenerRuleVerticalExtents(listenerGroup);

    return Math.max(0, -top);
  };

  const getEventListenerTopPadding = (eventGroup: any) => {
    return Math.max(0, ...eventGroup.listeners.map(getListenerTopPadding));
  };

  const renderListenerRuleChain = ({
    model,
    setupVariables,
    listenerGroup,
    listenerX,
    listenerY,
    previousEndPorts,
    componentUuid,
    workflowKey,
    startRuntimeSourcesByWorkflowKey,
    startBroadcastSourcesByWorkflowKey,
  }: any) => {
    const ruleEntryPorts: any[] = [];
    const actionChains: Array<{
      nodes: any[];
      firstInPort: any;
      lastOutPort: any;
    }> = [];
    const falseEndPorts: any[] = [];
    const branchEndPorts: any[] = [];
    const rules = listenerGroup.rules || [];
    const ruleStartX = listenerX + LAYOUT.listenerToRuleX;
    let nextRuleFlowX = ruleStartX;
    const ruleFlowXs = rules.map((branch: any) => {
      const flowX = nextRuleFlowX;
      const actionCount = Math.max(getRuleActions(branch?.rule).length, 1);
      nextRuleFlowX +=
        LAYOUT.ruleFalseBranchX +
        Math.max(0, actionCount - 1) * LAYOUT.actionToActionX;

      return flowX;
    });

    rules.forEach((branch: any, ruleIndex: number) => {
      const { rule } = branch;
      const conditions = getRuleConditions(rule);
      const actions = getRuleActions(rule);
      const shouldShiftTrueBranch =
        conditions.length && hasFalseBranch(rules, ruleIndex);
      const flowX = ruleFlowXs[ruleIndex];
      const branchY = listenerY + getRuleBranchOffsetY(ruleIndex);
      const actionX = conditions.length
        ? flowX + LAYOUT.decisionToActionX
        : flowX;
      const actionY = shouldShiftTrueBranch
        ? branchY - LAYOUT.decisionTrueBranchRise
        : branchY;

      if (conditions.length) {
        const decisionLogicNodeKey = [
          "condition",
          componentUuid || listenerGroup.componentIndex || "component",
          listenerGroup.workflow?.UUID ?? workflowKey ?? "workflow",
          listenerGroup.listener?.UUID ??
            listenerGroup.listenerSequence ??
            "listener",
          rule?.UUID ?? branch.ruleIndex ?? ruleIndex ?? "rule",
          "decision",
        ].join(":");
        const decisionNode = createComponentLogicNode({
          nodeKind: "decision",
          title: getConditionNodeTitle(conditions, branch),
          meta: `${conditions.length} condition(s)`,
          details: getRuleConditionDetails(conditions, branch),
          variables: getVariablesUsedIn(
            conditions,
            setupVariables,
            componentUuid
          ),
          icon: getConditionsFlowIcon(conditions),
          color: "#ff2aa3",
          x: flowX,
          y: branchY,
          logicNodeKey: decisionLogicNodeKey,
        });
        model.addNode(decisionNode);
        ruleEntryPorts.push(decisionNode.getPort("decision-in") as any);
      }

      const actionItems = actions.length ? actions : [null];
      const actionNodes = actionItems.map(
        (action: any, actionIndex: number) => {
          const actionDetails = action
            ? getActionDetails(action, actionIndex, branch)
            : [];
          const actionLogicNodeKey = [
            "action",
            componentUuid || listenerGroup.componentIndex || "component",
            listenerGroup.workflow?.UUID ?? workflowKey ?? "workflow",
            listenerGroup.listener?.UUID ??
              listenerGroup.listenerSequence ??
              "listener",
            rule?.UUID ?? branch.ruleIndex ?? ruleIndex ?? "rule",
            action?.UUID || actionIndex,
          ].join(":");
          const actionNode = createComponentLogicNode({
            nodeKind: "actionGroup",
            title: action
              ? getActionLabel(action, actionIndex, branch)
              : "No action configured",
            meta: actions.length
              ? `Action ${actionIndex + 1} of ${actions.length}`
              : "No actions",
            details: actionDetails.length
              ? actionDetails
              : ["No actions configured"],
            variables: action
              ? getVariablesUsedIn(action, setupVariables, componentUuid)
              : [],
            icon: action ? getActionFlowIcon(action) : undefined,
            color: "#22d3ee",
            x: actionX + actionIndex * LAYOUT.actionToActionX,
            y: actionY,
            logicNodeKey: action ? actionLogicNodeKey : undefined,
          });
          model.addNode(actionNode);

          return actionNode;
        }
      );

      actionNodes.forEach((actionNode: any, actionIndex: number) => {
        const nextActionNode = actionNodes[actionIndex + 1];
        if (!nextActionNode) return;

        model.addLink(
          createFlowLink(
            actionNode.getPort("actions-out") as any,
            nextActionNode.getPort("actions-in") as any,
            "#22d3ee"
          )
        );
      });

      actionChains.push({
        nodes: actionNodes,
        firstInPort: actionNodes[0].getPort("actions-in") as any,
        lastOutPort: actionNodes[actionNodes.length - 1].getPort(
          "actions-out"
        ) as any,
      });

      if (!conditions.length) {
        ruleEntryPorts.push(actionChains[ruleIndex].firstInPort);
      }
    });

    previousEndPorts.forEach((endPort: any) => {
      const firstRuleEntryPort = ruleEntryPorts[0];
      if (firstRuleEntryPort) {
        const sourcePort = getContinuationPort(endPort);
        const linkOptions = getContinuationLinkOptions(endPort);
        model.addLink(
          createFlowLink(
            sourcePort,
            firstRuleEntryPort,
            linkOptions.color,
            linkOptions.label,
            linkOptions.route
          )
        );
      }
    });

    rules.forEach((branch: any, ruleIndex: number) => {
      const { rule } = branch;
      const actionChain = actionChains[ruleIndex];
      const actionsIn = actionChain.firstInPort;
      const actionOut = actionChain.lastOutPort;
      const conditions = getRuleConditions(rule);
      const actions = getRuleActions(rule);
      const nextRuleEntryPort = ruleEntryPorts[ruleIndex + 1];

      if (conditions.length) {
        const decisionIn = ruleEntryPorts[ruleIndex];
        const decisionNode = decisionIn?.getNode?.();
        const decisionOut = decisionNode.getPort("decision-out") as any;

        model.addLink(
          createFlowLink(decisionOut, actionsIn, "#22d3ee", "true", "true")
        );

        if (nextRuleEntryPort) {
          model.addLink(
            createFlowLink(
              decisionOut,
              nextRuleEntryPort,
              "#ff2aa3",
              "false",
              "false"
            )
          );
        } else {
          falseEndPorts.push({
            port: decisionOut,
            color: "#ff2aa3",
            label: "false",
            route: "normal",
          });
        }
      } else if (nextRuleEntryPort) {
        model.addLink(createFlowLink(actionOut, nextRuleEntryPort, "#a855f7"));
      }

      actions.forEach((action: any, actionIndex: number) => {
        const actionSourcePort =
          actionChain.nodes[actionIndex]?.getPort("actions-out") || actionOut;
        const targetWorkflowKey = getStartRuntimeWorkflowKey(action);
        if (targetWorkflowKey) {
          const sourcePorts =
            startRuntimeSourcesByWorkflowKey.get(targetWorkflowKey) || [];
          sourcePorts.push(actionSourcePort);
          startRuntimeSourcesByWorkflowKey.set(targetWorkflowKey, sourcePorts);
        }

        if (isStartBroadcastAction(action)) {
          const sourcePorts =
            startBroadcastSourcesByWorkflowKey.get(workflowKey) || [];
          sourcePorts.push(actionSourcePort);
          startBroadcastSourcesByWorkflowKey.set(workflowKey, sourcePorts);
        }
      });

      branchEndPorts.push(actionOut);
    });

    const furthestActionX = actionChains
      .flatMap((actionChain) => actionChain.nodes)
      .reduce(
        (furthestX: number, actionNode: any) =>
          Math.max(furthestX, actionNode.position?.x ?? ruleStartX),
        ruleStartX
      );
    const nextListenerX = furthestActionX + LAYOUT.listenerToRuleX;

    return {
      endPorts: getListenerChainEndPorts(branchEndPorts, falseEndPorts),
      nextListenerX,
    };
  };

  const renderComponentLogicBranches = ({
    model,
    setupVariables,
    ruleComponents,
    ruleBaseX,
    nextRuleComponentY,
  }: any) => {
    let nextComponentY = nextRuleComponentY;
    const getListenerBranchSpan = (listenerGroup: any) => {
      const { bottom } = getListenerRuleVerticalExtents(listenerGroup);
      const topPadding = getListenerTopPadding(listenerGroup);

      return topPadding + bottom + LAYOUT.componentBranchGap;
    };
    const getEventBranchSpan = (eventGroup: any) => {
      const maxListenerSpan = Math.max(
        LAYOUT.eventBranchGap,
        ...eventGroup.listeners.map(getListenerBranchSpan)
      );
      return maxListenerSpan + LAYOUT.componentBranchGap;
    };
    const getWorkflowBranchSpan = (workflowGroup: any) => {
      const eventSpan = workflowGroup.events.reduce(
        (span: number, eventGroup: any) =>
          span + getEventBranchSpan(eventGroup),
        0
      );
      return Math.max(LAYOUT.eventBranchGap, eventSpan);
    };

    const getWorkflowTopPadding = (workflowGroup: any) => {
      return Math.max(
        0,
        ...workflowGroup.events.map(getEventListenerTopPadding)
      );
    };

    ruleComponents.forEach(({ component, componentIndex, branches }: any) => {
      const workflowGroups = groupBranchesByWorkflow(branches);
      const componentSpan = workflowGroups.reduce(
        (span: number, workflowGroup: any) =>
          span + getWorkflowBranchSpan(workflowGroup),
        LAYOUT.componentBranchGap
      );
      const componentY = nextComponentY;
      const workflowCount = getUniqueBranchCount(
        branches,
        getBranchWorkflowKey
      );
      const eventCount = getUniqueBranchCount(branches, getBranchEventKey);
      const listenerCount = getUniqueBranchCount(
        branches,
        getBranchListenerKey
      );
      const componentRole = getComponentRole(component);
      const componentUuid = stringifyValue(
        component?.ComponentUuid || component?.ItemUuid
      );
      const componentX = componentRole
        ? ruleBaseX + LAYOUT.roleToComponentX
        : ruleBaseX;
      let componentRoleOut: any = null;

      if (componentRole) {
        const componentRoleNode = createComponentLogicNode({
          nodeKind: "componentRole",
          title: getHumanLabel(componentRole),
          subtitle: "Component role",
          color: "#f59e0b",
          x: ruleBaseX,
          y: componentY,
        });
        componentRoleOut = componentRoleNode.getPort("role-out") as any;
        model.addNode(componentRoleNode);
      }

      const componentNode = createComponentLogicNode({
        nodeKind: "component",
        title:
          component?.Item?.Label ||
          component?.Label ||
          `Component ${componentIndex + 1}`,
        subtitle: component?.Item?.Description || component?.Description || "",
        meta: `${workflowCount} workflow(s), ${eventCount} event(s), ${listenerCount} listener(s), ${branches.length} rule branch(es)`,
        color: "#10b981",
        x: componentX,
        y: componentY,
      });
      const componentIn = componentNode.getPort("component-in") as any;
      const componentEventsOut = componentNode.getPort("events-out") as any;
      model.addNode(componentNode);
      if (componentRoleOut) {
        model.addLink(createFlowLink(componentRoleOut, componentIn, "#10b981"));
      }

      let workflowOffsetY = 0;
      const workflowInPortsByKey = new Map<string, any>();
      const broadcastEventInPortsByWorkflowKey = new Map<string, any[]>();
      const startRuntimeSourcesByWorkflowKey = new Map<string, any[]>();
      const startBroadcastSourcesByWorkflowKey = new Map<string, any[]>();

      workflowGroups.forEach((workflowGroup: any, workflowIndex: number) => {
        const workflowKey = workflowGroup.key;
        const workflowY =
          componentY + workflowOffsetY + getWorkflowTopPadding(workflowGroup);
        const workflowHasStartBroadcastAction = workflowGroup.branches.some(
          (branch: any) =>
            getRuleActions(branch?.rule).some(isStartBroadcastAction)
        );
        const workflowEventCount = Math.max(1, workflowGroup.events.length);
        const workflowListenerCount = getUniqueBranchCount(
          workflowGroup.branches,
          getBranchListenerKey
        );
        const workflowNode = createComponentLogicNode({
          nodeKind: "workflow",
          title: getWorkflowLabel(workflowGroup.sampleBranch, workflowIndex),
          subtitle: workflowGroup.workflow?.Description || "",
          meta: `${workflowEventCount} event(s), ${workflowListenerCount} listener(s)`,
          color: "#a855f7",
          x: componentX + LAYOUT.componentToEventX,
          y: workflowY,
        });
        const workflowIn = workflowNode.getPort("workflow-in") as any;
        const workflowEventsOut = workflowNode.getPort("events-out") as any;
        model.addNode(workflowNode);
        getWorkflowKeyAliases(workflowGroup.workflow, workflowIndex).forEach(
          (workflowKeyAlias) => {
            workflowInPortsByKey.set(workflowKeyAlias, workflowIn);
          }
        );

        if (
          isEntrypointWorkflow(workflowGroup.workflow) ||
          workflowIndex === 0
        ) {
          model.addLink(
            createFlowLink(componentEventsOut, workflowIn, "#a855f7")
          );
        }

        let eventOffsetY = 0;

        workflowGroup.events.forEach((eventGroup: any, eventIndex: number) => {
          const listenerCountForEvent = Math.max(
            1,
            eventGroup.listeners.length
          );
          const eventY = workflowY + eventOffsetY;
          const eventNode = createComponentLogicNode({
            nodeKind: "event",
            title: getEventLabel(eventGroup.sampleBranch, eventIndex),
            meta: `${listenerCountForEvent} listener(s)`,
            color: "#38bdf8",
            x: componentX + LAYOUT.componentToEventX + LAYOUT.workflowToEventX,
            y: eventY,
          });
          const eventIn = eventNode.getPort("event-in") as any;
          const eventListenersOut = eventNode.getPort("listeners-out") as any;
          model.addNode(eventNode);
          if (isBroadcastFollowUpEvent(eventGroup.eventType)) {
            const eventInPorts =
              broadcastEventInPortsByWorkflowKey.get(workflowKey) || [];
            eventInPorts.push(eventIn);
            broadcastEventInPortsByWorkflowKey.set(workflowKey, eventInPorts);
          }

          if (
            !isBroadcastFollowUpEvent(eventGroup.eventType) ||
            !workflowHasStartBroadcastAction
          ) {
            model.addLink(
              createFlowLink(workflowEventsOut, eventIn, "#38bdf8")
            );
          }

          let previousListenerEndPorts: any[] = [eventListenersOut];
          let listenerX =
            componentX +
            LAYOUT.componentToEventX +
            LAYOUT.workflowToEventX +
            LAYOUT.eventToListenerX;

          eventGroup.listeners.forEach((listenerGroup: any) => {
            const listenerY = eventY;
            const listenerChain = renderListenerRuleChain({
              model,
              setupVariables,
              listenerGroup,
              listenerX,
              listenerY,
              previousEndPorts: previousListenerEndPorts,
              componentUuid,
              workflowKey,
              startRuntimeSourcesByWorkflowKey,
              startBroadcastSourcesByWorkflowKey,
            });
            previousListenerEndPorts = listenerChain.endPorts;
            listenerX = listenerChain.nextListenerX;
          });

          eventOffsetY += getEventBranchSpan(eventGroup);
        });

        workflowOffsetY += getWorkflowBranchSpan(workflowGroup);
      });

      startRuntimeSourcesByWorkflowKey.forEach(
        (sourcePorts: any[], targetWorkflowKey: string) => {
          const targetWorkflowIn = workflowInPortsByKey.get(targetWorkflowKey);
          if (!targetWorkflowIn) return;

          sourcePorts.forEach((sourcePort: any) => {
            const runtimeSourcePort = getDownwardSourcePort(
              sourcePort,
              targetWorkflowIn
            );

            model.addLink(
              createFlowLink(
                runtimeSourcePort,
                targetWorkflowIn,
                "#a855f7",
                "start",
                "rectangular"
              )
            );
          });
        }
      );

      startBroadcastSourcesByWorkflowKey.forEach(
        (sourcePorts: any[], sourceWorkflowKey: string) => {
          const targetEventInPorts =
            broadcastEventInPortsByWorkflowKey.get(sourceWorkflowKey) || [];

          sourcePorts.forEach((sourcePort: any) => {
            targetEventInPorts.forEach((targetEventIn: any) => {
              const broadcastSourcePort = getDownwardSourcePort(
                sourcePort,
                targetEventIn
              );

              model.addLink(
                createFlowLink(
                  broadcastSourcePort,
                  targetEventIn,
                  "#f59e0b",
                  "broadcast",
                  "rectangular"
                )
              );
            });
          });
        }
      );

      nextComponentY += componentSpan;
    });

    return nextComponentY;
  };

  const engine = useMemo(() => {
    const diagramEngine = createEngine();
    diagramEngine.setModel(new DiagramModel());
    diagramEngine.getNodeFactories().registerFactory(
      new SolutionFlowNodeFactory((selection: any) => {
        diagramNodeClickRef.current(selection);
      }) as any
    );

    return diagramEngine;
  }, []);

  const refreshLinkGeometry = useCallback((diagramEngine: any) => {
    const canvas = diagramEngine?.getCanvas?.();
    if (!canvas?.offsetWidth || !canvas?.offsetHeight) {
      return false;
    }

    const currentModel = diagramEngine?.getModel?.() || diagramEngine?.model;

    (currentModel?.getNodes?.() || []).forEach((node: any) => {
      Object.values(node.getPorts?.() || {}).forEach((port: any) => {
        if (port?.reportedPosition) {
          port.reportPosition?.();
        }
      });
    });

    const linkFactories =
      diagramEngine?.getLinkFactories?.()?.getFactories?.() || {};
    Object.values(linkFactories).forEach((factory: any) => {
      if (Array.isArray(factory?.canvasMatrix)) {
        factory.canvasMatrix = [];
      }
      if (Array.isArray(factory?.routingMatrix)) {
        factory.routingMatrix = [];
      }
      factory?.calculateRoutingMatrix?.();
    });

    return true;
  }, []);

  useEffect(() => {
    onNodeClickRef.current = onNodeClick;
  }, [onNodeClick]);

  const structureSignature = useMemo(() => {
    return JSON.stringify({
      viewMode,
      setup: showSolutionSteps
        ? steps.map((step: any, stepIndex: number) => ({
            step: step?.id || step?.UUID || step?.name || stepIndex,
            groups: (step?.inputGroups || []).map(
              (group: any, groupIndex: number) => ({
                group: group?.id || group?.UUID || group?.name || groupIndex,
                inputs: (group?.inputs || []).map(
                  (input: any, inputIndex: number) =>
                    input?.UUID ||
                    (input?.ComponentUuid && input?.Field
                      ? `${input.ComponentUuid}:${input.Field}`
                      : null) ||
                    input?.Name ||
                    input?.Label ||
                    inputIndex
                ),
              })
            ),
          }))
        : [],
      logic: showComponentFlow
        ? getComponentLogicBranches(solution).map(
            getComponentStructureSignature
          )
        : [],
    });
  }, [showComponentFlow, showSolutionSteps, solution, steps, viewMode]);

  const model = useMemo(() => {
    focusTargetRef.current = {};

    const model = new DiagramModel();
    const setupVariables = collectVariableReferences(solution, steps);
    const ruleComponents = getComponentLogicBranches(solution);

    let solutionOut: any = null;

    if (showSolutionSteps) {
      const solutionNode = new SolutionFlowNodeModel({
        nodeKind: "solution",
        title: solution?.Label || "Solution",
        subtitle: solution?.Reference || "",
        color: "rgb(42, 112, 181)",
        flowSelection: { nodeKind: "solution" as const },
      });
      solutionNode.setPosition(60, 40);
      solutionOut = solutionNode.getPort("steps-out") as any;
      model.addNode(solutionNode);
    }

    const stepPorts: Array<{ inPort: any; outPort: any; groupsOutPort: any }> =
      [];
    const inputLookup = new Map<string, { inPort: any; outPort: any }>();
    const visibilitySourcesByField = new Map<
      string,
      Array<{ y: number; outPort: any }>
    >();
    const visibilityDependents: Array<{
      y: number;
      visibilityField: string;
      inPort: any;
    }> = [];

    const getInputRowCount = (group: any) => {
      return Math.max(
        1,
        Math.ceil((group?.inputs?.length || 0) / LAYOUT.maxInputsPerRow)
      );
    };

    const getGroupVisualHeight = (group: any) => {
      return (
        LAYOUT.groupRowHeight +
        (getInputRowCount(group) - 1) * LAYOUT.inputVerticalGap
      );
    };

    const getGroupStackSpan = (group: any) => {
      return Math.max(
        LAYOUT.groupVerticalGap,
        getGroupVisualHeight(group) + LAYOUT.groupBottomGap
      );
    };

    const getStepRequiredSpan = (step: any) => {
      const groups = step?.inputGroups || [];
      if (groups.length === 0) {
        return LAYOUT.minStepSpan;
      }

      let groupOffsetY = 0;
      let groupsBlockHeight = 0;
      groups.forEach((group: any) => {
        groupsBlockHeight = Math.max(
          groupsBlockHeight,
          groupOffsetY + getGroupVisualHeight(group)
        );
        groupOffsetY += getGroupStackSpan(group);
      });

      return Math.max(LAYOUT.minStepSpan, groupsBlockHeight + 40);
    };

    let nextStepY = LAYOUT.baseY;
    let nextRuleComponentY = LAYOUT.ruleBaseY;

    if (showSolutionSteps)
      steps.forEach((step: any, stepIdx: number) => {
        const stepRequiredSpan = getStepRequiredSpan(step);
        const stepHasVisibilityDependency = !!step.VisibilityField;
        const stepNode = new SolutionFlowNodeModel({
          nodeKind: "step",
          title: `Step ${stepIdx + 1}: ${step.name || "Untitled"}`,
          subtitle: step.description || "",
          meta: `${step.inputGroups?.length || 0} group(s)`,
          color:
            stepIdx === activeStepIndex
              ? "rgb(108, 92, 231)"
              : "rgb(72, 71, 95)",
          flowSelection: {
            nodeKind: "step" as const,
            stepIndex: stepIdx,
          },
        });
        const stepX = LAYOUT.baseX;
        const stepY = nextStepY;
        stepNode.setPosition(stepX, stepY);
        const stepIn = stepNode.getPort("step-in") as any;
        const stepOut = stepNode.getPort("step-out") as any;
        const groupsOutPort = stepNode.getPort("groups-out") as any;
        const stepVisibilityIn = stepNode.getPort("visibility-in") as any;
        model.addNode(stepNode);
        stepPorts.push({ inPort: stepIn, outPort: stepOut, groupsOutPort });

        focusTargetRef.current[`step:${stepIdx}`] = {
          x: stepX,
          y: stepY,
          width: 260,
          height: 120,
        };

        if (step.VisibilityField) {
          visibilityDependents.push({
            y: stepY,
            visibilityField: String(step.VisibilityField),
            inPort: stepVisibilityIn,
          });
        }

        if (stepIdx === 0 && !stepHasVisibilityDependency) {
          const solutionToStep = solutionOut.link(stepIn);
          Object.assign(solutionToStep.getOptions() as any, {
            color: "#64748b",
            width: 2,
          });
          model.addLink(solutionToStep);
        }

        if (stepIdx > 0 && !stepHasVisibilityDependency) {
          const prevToCurrent = stepPorts[stepIdx - 1].outPort.link(stepIn);
          Object.assign(prevToCurrent.getOptions() as any, {
            color: "#0d6efd",
            width: 2,
          });
          model.addLink(prevToCurrent);
        }

        let groupOffsetY = 0;

        (step.inputGroups || []).forEach((group: any, groupIdx: number) => {
          const groupHasVisibilityDependency = !!group.VisibilityField;
          const groupNode = new SolutionFlowNodeModel({
            nodeKind: "group",
            title: group.name || `Group ${groupIdx + 1}`,
            subtitle: group.description || "",
            meta: `${group.inputs?.length || 0} input(s)`,
            color: "rgb(22, 163, 74)",
            flowSelection: {
              nodeKind: "group" as const,
              stepIndex: stepIdx,
              groupId: group.id,
            },
          });
          const groupX = stepX + LAYOUT.stepToGroupX;
          const groupY = stepY + groupOffsetY;
          groupNode.setPosition(groupX, groupY);
          const groupIn = groupNode.getPort("group-in") as any;
          const groupOut = groupNode.getPort("inputs-out") as any;
          const groupVisibilityIn = groupNode.getPort("visibility-in") as any;
          model.addNode(groupNode);

          if (group.id) {
            focusTargetRef.current[`step:${stepIdx}|group:${group.id}`] = {
              x: groupX,
              y: groupY,
              width: 240,
              height: 120,
            };
          }

          if (group.VisibilityField) {
            visibilityDependents.push({
              y: groupY,
              visibilityField: String(group.VisibilityField),
              inPort: groupVisibilityIn,
            });
          }

          if (!groupHasVisibilityDependency) {
            const stepToGroup = groupsOutPort.link(groupIn);
            Object.assign(stepToGroup.getOptions() as any, {
              color: "#16a34a",
              width: 2,
            });
            model.addLink(stepToGroup);
          }

          let previousSequentialInputOut: any = null;
          let isFirstNonVisibilityInput = true;

          (group.inputs || []).forEach((input: any, inputIdx: number) => {
            const inputHasVisibilityDependency = !!input.VisibilityField;
            const inputFocusKey = getInputFocusKey(
              stepIdx,
              group.id,
              input,
              inputIdx
            );
            const inputNode = new SolutionFlowNodeModel({
              nodeKind: "input",
              title: input.Label || input.Name || `Input ${inputIdx + 1}`,
              subtitle: input.Description || "",
              meta: input.Field ? `Field: ${input.Field}` : "",
              color: "rgb(148, 163, 184)",
              flowSelection: {
                nodeKind: "input" as const,
                stepIndex: stepIdx,
                groupId: group.id,
                inputIndex: inputIdx,
                inputFocusKey: inputFocusKey || undefined,
              },
            });
            const inputColumn = inputIdx % LAYOUT.maxInputsPerRow;
            const inputRow = Math.floor(inputIdx / LAYOUT.maxInputsPerRow);
            const inputX =
              groupX +
              LAYOUT.groupToInputX +
              inputColumn * LAYOUT.inputHorizontalGap;
            const inputY = groupY + inputRow * LAYOUT.inputVerticalGap;
            inputNode.setPosition(inputX, inputY);
            const inputIn = inputNode.getPort("input-in") as any;
            const inputOut = inputNode.getPort("input-out") as any;
            const inputVisibilityIn = inputNode.getPort("visibility-in") as any;
            const inputVisibilityOut = inputNode.getPort(
              "visibility-out"
            ) as any;
            model.addNode(inputNode);

            if (inputFocusKey) {
              focusTargetRef.current[inputFocusKey] = {
                x: inputX,
                y: inputY,
                width: 240,
                height: 120,
              };
            }

            if (!inputHasVisibilityDependency) {
              if (isFirstNonVisibilityInput) {
                const groupToInput = groupOut.link(inputIn);
                Object.assign(groupToInput.getOptions() as any, {
                  color: "#94a3b8",
                  width: 2,
                });
                model.addLink(groupToInput);
                isFirstNonVisibilityInput = false;
              }

              if (previousSequentialInputOut) {
                const sequentialInputLink =
                  previousSequentialInputOut.link(inputIn);
                Object.assign(sequentialInputLink.getOptions() as any, {
                  color: "#64748b",
                  width: 2,
                });
                model.addLink(sequentialInputLink);
              }

              previousSequentialInputOut = inputOut;
            } else {
              // Keep visibility-dependent inputs detached from structural chains.
              previousSequentialInputOut = null;
            }

            if (input.UUID) {
              inputLookup.set(`uuid:${input.UUID}`, {
                inPort: inputIn,
                outPort: inputOut,
              });
            }
            if (input.ComponentUuid && input.Field) {
              inputLookup.set(`field:${input.ComponentUuid}.${input.Field}`, {
                inPort: inputIn,
                outPort: inputOut,
              });
            }
            if (input.ComponentUuid && input.UUID) {
              inputLookup.set(
                `component:${input.ComponentUuid}.${input.UUID}`,
                {
                  inPort: inputIn,
                  outPort: inputOut,
                }
              );
            }

            if (input.Field) {
              const fieldKey = String(input.Field);
              const existingSources =
                visibilitySourcesByField.get(fieldKey) || [];
              existingSources.push({ y: inputY, outPort: inputVisibilityOut });
              visibilitySourcesByField.set(fieldKey, existingSources);
            }

            if (input.VisibilityField) {
              visibilityDependents.push({
                y: inputY,
                visibilityField: String(input.VisibilityField),
                inPort: inputVisibilityIn,
              });
            }

            (input.MergedInputRefs || []).forEach(
              (ref: any, refIdx: number) => {
                if (inputHasVisibilityDependency) return;
                const byField =
                  ref.ComponentUuid && ref.Field
                    ? inputLookup.get(`field:${ref.ComponentUuid}.${ref.Field}`)
                    : null;
                const byUuid = ref.UUID
                  ? inputLookup.get(`uuid:${ref.UUID}`)
                  : null;
                const byComponentUuid =
                  ref.ComponentUuid && ref.UUID
                    ? inputLookup.get(
                        `component:${ref.ComponentUuid}.${ref.UUID}`
                      )
                    : null;
                const sourceInput = byField || byUuid || byComponentUuid;

                if (!sourceInput || sourceInput.inPort === inputIn) return;

                const mergeLink = sourceInput.outPort.link(inputIn);
                Object.assign(mergeLink.getOptions() as any, {
                  color: "#f59e0b",
                  width: 1,
                });
                mergeLink.addLabel(`merge ${refIdx + 1}`);
                model.addLink(mergeLink);
              }
            );
          });

          groupOffsetY += getGroupStackSpan(group);
        });

        nextStepY += stepRequiredSpan + LAYOUT.stepVerticalGap;
      });

    if (showComponentFlow) {
      const ruleBaseX = showSolutionSteps
        ? LAYOUT.baseX + LAYOUT.stepToGroupX + LAYOUT.groupToInputX + 920
        : LAYOUT.baseX;

      nextRuleComponentY = renderComponentLogicBranches({
        model,
        setupVariables,
        ruleComponents,
        ruleBaseX,
        nextRuleComponentY,
      });
    }

    if (showSolutionSteps)
      visibilityDependents.forEach((dependent) => {
        const candidates =
          visibilitySourcesByField.get(dependent.visibilityField) || [];
        if (!candidates.length) return;

        const source =
          [...candidates]
            .filter((candidate) => candidate.y < dependent.y)
            .sort((a, b) => b.y - a.y)[0] || candidates[0];

        if (!source?.outPort || !dependent.inPort) return;

        const visibilityLink = source.outPort.link(dependent.inPort);
        Object.assign(visibilityLink.getOptions() as any, {
          color: "#ec4899",
          width: 2,
        });
        visibilityLink.addLabel("visibility");
        model.addLink(visibilityLink);
      });

    return model;
  }, [
    activeStepIndex,
    showComponentFlow,
    showSolutionSteps,
    solution,
    solution?.Label,
    solution?.Reference,
    steps,
  ]);

  useLayoutEffect(() => {
    const diagramEngine = engine as any;
    const previousModel = diagramEngine?.getModel?.() || diagramEngine?.model;

    if (
      previousModel &&
      previousModel !== model &&
      !pendingInitialFitRef.current
    ) {
      stopViewportAnimation();
      setViewport(
        model,
        previousModel.getOffsetX?.() || 0,
        previousModel.getOffsetY?.() || 0,
        previousModel.getZoomLevel?.() || 100
      );
    }

    let isCancelled = false;
    diagramEngine.setModel(model);
    const repaintResult = diagramEngine.repaintCanvas?.(true);

    Promise.resolve(repaintResult).then(() => {
      if (isCancelled) return;

      refreshLinkGeometry(diagramEngine);
      diagramEngine.repaintCanvas?.();
    });

    const repaintFrame = requestAnimationFrame(() => {
      if (isCancelled) return;

      refreshLinkGeometry(diagramEngine);
      diagramEngine.repaintCanvas?.();
    });

    return () => {
      isCancelled = true;
      cancelAnimationFrame(repaintFrame);
    };
  }, [engine, model, refreshLinkGeometry]);

  useEffect(() => {
    const diagramEngine = engine as any;
    const currentModel = diagramEngine?.getModel?.() || diagramEngine?.model;
    if (!currentModel) return;

    (currentModel.getNodes?.() || []).forEach((node: any) => {
      const selection = node?.options?.flowSelection;
      if (selection?.nodeKind !== "step") return;

      node.options.color =
        selection.stepIndex === activeStepIndex
          ? "rgb(108, 92, 231)"
          : "rgb(72, 71, 95)";
    });

    diagramEngine.repaintCanvas?.();
  }, [activeStepIndex, engine, model]);

  useEffect(() => {
    const diagramEngine = engine as any;
    const currentModel = diagramEngine?.getModel?.() || diagramEngine?.model;
    if (!currentModel) return;

    const resolvedFocusKey =
      (focusedInputNodeKey && focusTargetRef.current[focusedInputNodeKey]
        ? focusedInputNodeKey
        : null) ||
      (focusedInputGroupId && focusTargetRef.current[focusedInputGroupId]
        ? focusedInputGroupId
        : null);

    (currentModel.getNodes?.() || []).forEach((node: any) => {
      const selection = node?.options?.flowSelection;
      const logicNodeKey = node?.options?.logicNodeKey || null;
      const isLogicFocused =
        !!focusedLogicNodeKey && logicNodeKey === focusedLogicNodeKey;

      if (!selection) {
        if (!isLogicFocused && node.isSelected?.()) {
          node.setSelected?.(false);
        }

        if (node.options.isFlowFocused !== isLogicFocused) {
          node.options.isFlowFocused = isLogicFocused;
          node.fireEvent?.(
            { isFlowFocused: isLogicFocused },
            "flowFocusChanged"
          );
        }
        return;
      }

      const nodeFocusKey =
        selection.nodeKind === "input"
          ? selection.inputFocusKey || null
          : selection.nodeKind === "group" &&
            selection.groupId !== undefined &&
            selection.stepIndex !== undefined
          ? `step:${selection.stepIndex}|group:${selection.groupId}`
          : null;

      const isFlowFocused =
        isLogicFocused ||
        (!!resolvedFocusKey && nodeFocusKey === resolvedFocusKey);
      if (!isFlowFocused && node.isSelected?.()) {
        node.setSelected?.(false);
      }

      if (node.options.isFlowFocused !== isFlowFocused) {
        node.options.isFlowFocused = isFlowFocused;
        node.fireEvent?.({ isFlowFocused }, "flowFocusChanged");
      }
    });

    diagramEngine.repaintCanvas?.();
  }, [
    engine,
    model,
    focusedInputGroupId,
    focusedInputNodeKey,
    focusedLogicNodeKey,
  ]);

  const stopViewportAnimation = () => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  const setViewport = (
    model: any,
    offsetX: number,
    offsetY: number,
    zoomLevel?: number
  ) => {
    if (typeof zoomLevel === "number") {
      model.setZoomLevel?.(zoomLevel);
    }

    if (typeof model.setOffset === "function") {
      model.setOffset(offsetX, offsetY);
    } else {
      model.setOffsetX?.(offsetX);
      model.setOffsetY?.(offsetY);
    }
  };

  const animateViewport = ({
    toOffsetX,
    toOffsetY,
    toZoom,
    duration = 280,
  }: {
    toOffsetX: number;
    toOffsetY: number;
    toZoom?: number;
    duration?: number;
  }) => {
    const diagramEngine = engine as any;
    const model = diagramEngine?.getModel?.() || diagramEngine?.model;
    if (!model) return;

    stopViewportAnimation();

    const fromOffsetX = model.getOffsetX?.() || 0;
    const fromOffsetY = model.getOffsetY?.() || 0;
    const fromZoom = model.getZoomLevel?.() || 100;
    const resolvedToZoom = typeof toZoom === "number" ? toZoom : fromZoom;

    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = duration <= 0 ? 1 : Math.min(1, elapsed / duration);
      const eased = easeOutCubic(progress);

      const nextOffsetX = fromOffsetX + (toOffsetX - fromOffsetX) * eased;
      const nextOffsetY = fromOffsetY + (toOffsetY - fromOffsetY) * eased;
      const nextZoom = fromZoom + (resolvedToZoom - fromZoom) * eased;

      setViewport(model, nextOffsetX, nextOffsetY, nextZoom);
      diagramEngine.repaintCanvas?.();

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(tick);
      } else {
        animationFrameRef.current = null;
      }
    };
    animationFrameRef.current = requestAnimationFrame(tick);
  };

  const getFitViewport = (margin = 28, targetModel?: any) => {
    const diagramEngine = engine as any;
    const model =
      targetModel || diagramEngine?.getModel?.() || diagramEngine?.model;
    const container = diagramContainerRef.current;
    if (!model || !container) return null;

    const nodes = model.getNodes?.() || [];
    if (!nodes.length) return null;

    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    nodes.forEach((node: any) => {
      const x = node?.position?.x ?? 0;
      const y = node?.position?.y ?? 0;
      const { width, height } = getNodeSize(node);
      const portMaxX = Object.values(node.getPorts?.() || {}).reduce(
        (portMax: number, port: any) => {
          const portX = port?.reportedPosition?.x;
          return typeof portX === "number" ? Math.max(portMax, portX) : portMax;
        },
        x + width
      );

      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x + width, portMaxX);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y + height);
    });

    const boundsWidth = Math.max(1, maxX - minX);
    const boundsHeight = Math.max(1, maxY - minY);
    const availableWidth = Math.max(1, container.clientWidth - margin * 2);
    const availableHeight = Math.max(1, container.clientHeight - margin * 2);
    const fitZoom = Math.min(
      100,
      (availableWidth / boundsWidth) * 100,
      (availableHeight / boundsHeight) * 100
    );

    const centerX = minX + boundsWidth / 2;
    const centerY = minY + boundsHeight / 2;
    const offsetX = container.clientWidth / 2 - (centerX * fitZoom) / 100;
    const offsetY = container.clientHeight / 2 - (centerY * fitZoom) / 100;

    return {
      zoom: fitZoom,
      offsetX,
      offsetY,
    };
  };

  useEffect(() => {
    if (focusedLogicNodeKey) return;

    const targetKey =
      (focusedInputNodeKey && focusTargetRef.current[focusedInputNodeKey]
        ? focusedInputNodeKey
        : null) ||
      (focusedInputGroupId && focusTargetRef.current[focusedInputGroupId]
        ? focusedInputGroupId
        : null) ||
      (typeof activeStepIndex === "number" &&
      focusTargetRef.current[`step:${activeStepIndex}`]
        ? `step:${activeStepIndex}`
        : null);
    if (!targetKey) return;

    if (
      focusedInputNodeKey &&
      targetKey === focusedInputNodeKey &&
      (suppressNextInputFocusViewportRef.current === true ||
        suppressNextInputFocusViewportRef.current === focusedInputNodeKey)
    ) {
      suppressNextInputFocusViewportRef.current = false;
      return;
    }

    const target = focusTargetRef.current[targetKey];
    if (!target) return;

    const diagramEngine = engine as any;
    const model = diagramEngine?.getModel?.() || diagramEngine?.model;
    const container = diagramContainerRef.current;
    if (!model || !container) return;

    const zoom = model.getZoomLevel?.() || 100;
    const offsetX = model.getOffsetX?.() || 0;
    const offsetY = model.getOffsetY?.() || 0;
    const isFocusedInput =
      !!focusedInputNodeKey && targetKey === focusedInputNodeKey;
    const targetZoom = isFocusedInput && zoom < 50 ? 50 : zoom;
    const zoomScale = targetZoom / 100;
    const targetLeft = target.x * zoomScale + offsetX;
    const targetRight = (target.x + target.width) * zoomScale + offsetX;
    const targetTop = target.y * zoomScale + offsetY;
    const targetBottom = (target.y + target.height) * zoomScale + offsetY;
    const viewLeft = FOCUS_VIEW_MARGIN;
    const viewRight = container.clientWidth - FOCUS_VIEW_MARGIN;
    const viewTop = FOCUS_VIEW_MARGIN;
    const viewBottom = container.clientHeight - FOCUS_VIEW_MARGIN;

    let nextOffsetX = offsetX;
    let nextOffsetY = offsetY;

    if (targetLeft < viewLeft) {
      nextOffsetX += viewLeft - targetLeft;
    } else if (targetRight > viewRight) {
      nextOffsetX -= targetRight - viewRight;
    }

    if (targetTop < viewTop) {
      nextOffsetY += viewTop - targetTop;
    } else if (targetBottom > viewBottom) {
      nextOffsetY -= targetBottom - viewBottom;
    }

    const shouldChangeZoom = targetZoom !== zoom;
    if (nextOffsetX === offsetX && nextOffsetY === offsetY && !shouldChangeZoom)
      return;

    animateViewport({
      toOffsetX: nextOffsetX,
      toOffsetY: nextOffsetY,
      toZoom: shouldChangeZoom ? targetZoom : undefined,
      duration: 240,
    });
  }, [
    activeStepIndex,
    engine,
    focusedInputGroupId,
    focusedInputNodeKey,
    focusedLogicNodeKey,
  ]);

  useEffect(() => {
    if (!focusedLogicNodeKey) return;

    const diagramEngine = engine as any;
    const model = diagramEngine?.getModel?.() || diagramEngine?.model;
    const container = diagramContainerRef.current;
    if (!model || !container) return;

    const targetNode = (model.getNodes?.() || []).find(
      (node: any) => node?.options?.logicNodeKey === focusedLogicNodeKey
    );
    if (!targetNode) return;

    const { width, height } = getNodeSize(targetNode);
    const zoom = Math.max(model.getZoomLevel?.() || 100, 70);
    const centerX = (targetNode.position?.x ?? 0) + width / 2;
    const centerY = (targetNode.position?.y ?? 0) + height / 2;

    animateViewport({
      toOffsetX: container.clientWidth / 2 - (centerX * zoom) / 100,
      toOffsetY: container.clientHeight / 2 - (centerY * zoom) / 100,
      toZoom: zoom,
      duration: 260,
    });
  }, [engine, focusedLogicNodeKey, model]);

  const zoomToFitDiagram = (animate = true, targetModel?: any) => {
    const fitTarget = getFitViewport(28, targetModel);
    if (!fitTarget) return;

    if (!animate) {
      const diagramEngine = engine as any;
      const model =
        targetModel || diagramEngine?.getModel?.() || diagramEngine?.model;
      if (!model) return;

      stopViewportAnimation();
      setViewport(model, fitTarget.offsetX, fitTarget.offsetY, fitTarget.zoom);
      pendingInitialFitRef.current = false;
      diagramEngine.repaintCanvas?.();
      return;
    }

    animateViewport({
      toOffsetX: fitTarget.offsetX,
      toOffsetY: fitTarget.offsetY,
      toZoom: fitTarget.zoom,
      duration: 320,
    });
    pendingInitialFitRef.current = false;
  };

  useLayoutEffect(() => {
    if (!steps.length) {
      previousStructureSignatureRef.current = structureSignature;
      return;
    }

    const isInitialFit = previousStructureSignatureRef.current === null;

    if (
      !isInitialFit &&
      previousStructureSignatureRef.current === structureSignature
    ) {
      return;
    }

    previousStructureSignatureRef.current = structureSignature;

    const frames: number[] = [];
    const scheduleFrame = (callback: FrameRequestCallback) => {
      const frame = requestAnimationFrame(callback);
      frames.push(frame);
      return frame;
    };

    const fitWhenReady = (attempt = 0) => {
      const container = diagramContainerRef.current;
      if (
        container?.clientWidth &&
        container?.clientHeight &&
        refreshLinkGeometry(engine as any)
      ) {
        zoomToFitDiagram(!isInitialFit, model);
        return;
      }

      if (attempt < 6) {
        scheduleFrame(() => fitWhenReady(attempt + 1));
      }
    };

    if (isInitialFit) {
      fitWhenReady();
      return () => {
        frames.forEach((frame) => cancelAnimationFrame(frame));
      };
    }

    const firstFrame = requestAnimationFrame(() => {
      refreshLinkGeometry(engine as any);
      frames.push(
        requestAnimationFrame(() => {
          refreshLinkGeometry(engine as any);
          scheduleFrame(() => fitWhenReady());
        })
      );
    });
    frames.push(firstFrame);

    return () => {
      frames.forEach((frame) => cancelAnimationFrame(frame));
    };
  }, [engine, model, refreshLinkGeometry, steps.length, structureSignature]);

  const zoomKeepingPoint = (
    clientX: number,
    clientY: number,
    targetZoom: number,
    duration = 0
  ) => {
    const diagramEngine = engine as any;
    const model = diagramEngine?.getModel?.() || diagramEngine?.model;
    const container = diagramContainerRef.current;
    if (!model || !container) {
      return;
    }

    const rect = container.getBoundingClientRect();
    const pointerX = clientX - rect.left;
    const pointerY = clientY - rect.top;

    const zoomBefore = model.getZoomLevel?.() || 100;
    const offsetXBefore = model.getOffsetX?.() || 0;
    const offsetYBefore = model.getOffsetY?.() || 0;

    const modelX = ((pointerX - offsetXBefore) / zoomBefore) * 100;
    const modelY = ((pointerY - offsetYBefore) / zoomBefore) * 100;

    const nextOffsetX = pointerX - (modelX * targetZoom) / 100;
    const nextOffsetY = pointerY - (modelY * targetZoom) / 100;

    if (duration > 0) {
      stopViewportAnimation();

      const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
      const start = performance.now();

      const tick = (now: number) => {
        const elapsed = now - start;
        const progress = Math.min(1, elapsed / duration);
        const eased = easeOutCubic(progress);
        const nextZoom = zoomBefore + (targetZoom - zoomBefore) * eased;

        setViewport(
          model,
          pointerX - (modelX * nextZoom) / 100,
          pointerY - (modelY * nextZoom) / 100,
          nextZoom
        );
        diagramEngine.repaintCanvas?.();

        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(tick);
        } else {
          animationFrameRef.current = null;
        }
      };

      animationFrameRef.current = requestAnimationFrame(tick);
      return;
    }

    stopViewportAnimation();
    setViewport(model, nextOffsetX, nextOffsetY, targetZoom);
    diagramEngine.repaintCanvas?.();
  };

  diagramNodeClickRef.current = (selection: any) => {
    const diagramEngine = engine as any;
    const model = diagramEngine?.getModel?.() || diagramEngine?.model;
    const zoomBefore = model?.getZoomLevel?.() || 100;
    const isInputEditSelection =
      selection?.nodeKind === "input" && selection?.action === "edit";
    const shouldZoomToSelection = !isInputEditSelection;
    if (isInputEditSelection) {
      suppressNextInputFocusViewportRef.current =
        selection?.inputFocusKey || true;
    }
    if (
      shouldZoomToSelection &&
      zoomBefore < 50 &&
      typeof selection?.clientX === "number" &&
      typeof selection?.clientY === "number"
    ) {
      zoomKeepingPoint(selection.clientX, selection.clientY, 50, 220);
    }

    onNodeClickRef.current?.({
      ...selection,
      isFullscreen,
    });
  };

  const handleDiagramWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const diagramEngine = engine as any;
    const model = diagramEngine?.getModel?.() || diagramEngine?.model;
    if (!model) return;

    const zoomBefore = model.getZoomLevel?.() || 100;
    const zoomDelta = event.deltaY > 0 ? -8 : 8;
    const nextZoom = Math.max(20, Math.min(180, zoomBefore + zoomDelta));

    zoomKeepingPoint(event.clientX, event.clientY, nextZoom);
  };

  const handleWheelZoom = useCallback(
    (event: WheelEvent) => {
      const container = diagramContainerRef.current;
      const diagramEngine = engine as any;
      const model = diagramEngine?.getModel?.() || diagramEngine?.model;
      if (!container || !model) return;

      const rect = container.getBoundingClientRect();
      const isInsideDiagram =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;

      if (!isInsideDiagram) return;

      event.preventDefault();
      event.stopPropagation();

      const zoomBefore = model.getZoomLevel?.() || 100;
      const zoomDelta = event.deltaY > 0 ? -8 : 8;
      const nextZoom = Math.max(20, Math.min(180, zoomBefore + zoomDelta));

      zoomKeepingPoint(event.clientX, event.clientY, nextZoom);
    },
    [engine]
  );

  const handleDiagramDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const diagramEngine = engine as any;
    const model = diagramEngine?.getModel?.() || diagramEngine?.model;
    const container = diagramContainerRef.current;
    if (!model || !container) return;

    const target = e.target as HTMLElement | null;
    const nodeElement = target?.closest(
      '[data-solution-flow-node="true"]'
    ) as HTMLElement | null;
    if (!nodeElement) {
      zoomToFitDiagram();
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const nodeRect = nodeElement.getBoundingClientRect();
    const zoomBefore = model.getZoomLevel?.() || 100;
    const offsetX = model.getOffsetX?.() || 0;
    const offsetY = model.getOffsetY?.() || 0;
    const nodeCenterX = nodeRect.left - containerRect.left + nodeRect.width / 2;
    const nodeCenterY = nodeRect.top - containerRect.top + nodeRect.height / 2;
    const modelCenterX = ((nodeCenterX - offsetX) / zoomBefore) * 100;
    const modelCenterY = ((nodeCenterY - offsetY) / zoomBefore) * 100;
    const targetZoom = Math.min(
      180,
      Math.max(zoomBefore + 50, zoomBefore * 1.35)
    );

    animateViewport({
      toOffsetX: container.clientWidth / 2 - (modelCenterX * targetZoom) / 100,
      toOffsetY: container.clientHeight / 2 - (modelCenterY * targetZoom) / 100,
      toZoom: targetZoom,
      duration: 260,
    });
  };

  const handleToggleFullscreen = async () => {
    const element = diagramContainerRef.current;
    if (!element) return;

    if (document.fullscreenElement === element) {
      await document.exitFullscreen();
      return;
    }

    await element.requestFullscreen();
  };

  useEffect(() => {
    const frames: number[] = [];

    const scheduleZoomToFit = () => {
      const firstFrame = requestAnimationFrame(() => {
        const secondFrame = requestAnimationFrame(() => {
          refreshLinkGeometry(engine as any);
          zoomToFitDiagram();
        });
        frames.push(secondFrame);
      });
      frames.push(firstFrame);
    };

    const onFullscreenChange = () => {
      const nextIsFullscreen =
        document.fullscreenElement === diagramContainerRef.current;
      setIsFullscreen(nextIsFullscreen);

      if (nextIsFullscreen) {
        scheduleZoomToFit();
      }
    };

    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => {
      frames.forEach((frame) => cancelAnimationFrame(frame));
      document.removeEventListener("fullscreenchange", onFullscreenChange);
    };
  }, [engine, refreshLinkGeometry]);

  useEffect(() => {
    const container = diagramContainerRef.current;

    const wheelListenerOptions = { capture: true, passive: false };

    document.addEventListener("wheel", handleWheelZoom, wheelListenerOptions);
    return () => {
      document.removeEventListener(
        "wheel",
        handleWheelZoom,
        wheelListenerOptions
      );
    };
  }, [handleWheelZoom]);

  useEffect(() => {
    return () => {
      stopViewportAnimation();
    };
  }, []);

  return (
    <div className="px-4 pb-4 pt-3 h-100 d-flex flex-column overflow-hidden">
      {steps.length === 0 ? (
        <div className="text-secondary">
          No steps found in this solution yet.
        </div>
      ) : (
        <div
          ref={diagramContainerRef}
          className="solution-flow-overview-shell"
          style={{
            flex: isFullscreen ? undefined : "1 1 0",
            height: isFullscreen ? "100vh" : undefined,
            minHeight: isFullscreen ? undefined : 0,
            position: "relative",
            overscrollBehavior: "contain",
            touchAction: "none",
            overflow: "hidden",
          }}
          onDoubleClick={handleDiagramDoubleClick}
          onWheel={handleDiagramWheel}
        >
          <button
            type="button"
            className="btn btn-sm"
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
              color: "#f8f0ff",
              background: "rgba(55, 20, 88, 0.86)",
              border: "1px solid rgba(192, 132, 252, 0.45)",
              borderRadius: 999,
            }}
            onClick={handleToggleFullscreen}
          >
            {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          </button>
          <CanvasWidget
            engine={engine}
            className="h-100 diagram-container solution-flow-overview-canvas"
          />
        </div>
      )}
    </div>
  );
};

export default SolutionFlowOverview;
